<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="calendars-dashboard">
        <div class="dashboard-header">
          <h1>Calendari Operativi</h1>
          <p>Gestisci eventi tecnici, nutrizionali e commerciali in un unico spazio moderno e minimale.</p>
        </div>

        <div
          class="realtime-notification-region"
          role="region"
          aria-label="Aggiornamenti calendario"
          aria-live="polite"
          aria-atomic="true"
        >
          <Transition name="realtime-badge" mode="out-in">
            <aside
              v-if="realtimeNotification"
              :key="realtimeNotification.id"
              class="realtime-badge"
              :style="{ '--notification-accent': realtimeNotification.accent }"
              @mouseenter="pauseRealtimeNotification('hover')"
              @mouseleave="resumeRealtimeNotification('hover')"
              @focusin="pauseRealtimeNotification('focus')"
              @focusout="resumeRealtimeNotification('focus')"
            >
              <button
                v-if="realtimeNotification.target"
                type="button"
                class="realtime-badge__content realtime-badge__content--action"
                :aria-label="`Apri ${realtimeNotification.name} nel calendario`"
                @click="openRealtimeNotificationTarget"
              >
                <span class="realtime-badge__header">
                  <span class="material-icons realtime-badge__icon" aria-hidden="true">event_available</span>
                  <span class="realtime-badge__eyebrow">{{ realtimeNotification.title }}</span>
                </span>
                <strong class="realtime-badge__name">{{ realtimeNotification.name }}</strong>
                <span class="realtime-badge__meta">{{ realtimeNotification.meta }}</span>
                <span class="realtime-badge__cta">
                  {{ realtimeNotification.actionLabel }}
                  <span class="material-icons" aria-hidden="true">arrow_forward</span>
                </span>
              </button>
              <div v-else class="realtime-badge__content">
                <div class="realtime-badge__header">
                  <span class="material-icons realtime-badge__icon" aria-hidden="true">event_available</span>
                  <span class="realtime-badge__eyebrow">{{ realtimeNotification.title }}</span>
                </div>
                <strong class="realtime-badge__name">{{ realtimeNotification.name }}</strong>
                <span class="realtime-badge__meta">{{ realtimeNotification.meta }}</span>
              </div>
              <button
                type="button"
                class="realtime-badge__close"
                aria-label="Chiudi notifica"
                @click.stop="dismissRealtimeNotification"
              >
                <span class="material-icons" aria-hidden="true">close</span>
              </button>
            </aside>
          </Transition>
        </div>

        <div class="calendar-grid">
          <div class="calendar-switcher">
            <button
              v-for="calendar in calendars"
              :key="calendar.id"
              type="button"
              class="calendar-toggle"
              :class="{ 'calendar-toggle--active': calendar.id === activeCalendarKey }"
              :style="calendar.id === activeCalendarKey ? { '--accent': calendar.accent } : {}"
              @click="setActiveCalendar(calendar.id)"
            >
              {{ calendar.label }}
            </button>
          </div>

          <section
            class="calendar-card calendar-card--full"
            :class="{ 'calendar-card--week': isGridView }"
          >
            <header class="card-header calendari-eventi-calendar-header">
              <div class="title-group calendari-eventi-calendar-title-row">
                <span class="calendar-badge" :style="{ backgroundColor: activeCalendarConfig.accent }" />
                <h2 class="calendari-eventi-calendar-title">{{ activeCalendarConfig.label }}</h2>
              </div>
            </header>

            <div class="calendar-controls">
              <div class="view-toggle" role="group" aria-label="Vista calendario">
                <button
                  type="button"
                  class="view-toggle__btn"
                  :class="{ 'view-toggle__btn--active': isListView }"
                  @click="setCalendarView('list')"
                >
                  Elenco
                </button>
                <button
                  type="button"
                  class="view-toggle__btn"
                  :class="{ 'view-toggle__btn--active': showDayView }"
                  @click="setCalendarView('day')"
                >
                  Giorno
                </button>
                <button
                  type="button"
                  class="view-toggle__btn"
                  :class="{ 'view-toggle__btn--active': showWeekView }"
                  @click="setCalendarView('week')"
                >
                  Settimana
                </button>
              </div>
              <div class="calendar-controls__buttons">
                <button
                  type="button"
                  class="primary-button"
                  @click="openBlockModal(activeCalendarKey)"
                >
                  Nuovo blocco
                </button>
                <button
                  type="button"
                  class="primary-button"
                  @click="openEventModal(activeCalendarKey)"
                >
                  Nuovo evento
                </button>
              </div>
            </div>

            <div v-if="isListView" class="calendar-wrapper">
              <Calendar
                :key="activeCalendarKey"
                :initial-page="activeCalendarInitialPage"
                :attributes="activeCalendarAttributes"
                :columns="1"
                :rows="1"
                :min-date="minDate"
                :title-position="'left'"
                is-expanded
                class="calendar-widget"
                @dayclick="onDaySelect(activeCalendarKey, $event)"
                :style="{ width: '100%' }"
              />
            </div>

            <div class="event-list" :class="{ 'event-list--week': isGridView }">
              <div class="event-toolbar">
                <div class="event-toolbar__info">
                  <h3>{{ eventToolbarTitle }}</h3>
                  <span v-if="eventToolbarHint" class="event-toolbar__hint">{{ eventToolbarHint }}</span>
                </div>
                <div class="event-toolbar__actions calendari-eventi-toolbar__filters">
                  <div class="event-search calendari-eventi-toolbar__control calendari-eventi-toolbar__search">
                    <span class="material-icons event-search__icon" aria-hidden="true">search</span>
                    <input
                      v-model.trim="eventSearchTerm"
                      type="search"
                      class="event-search__input calendari-eventi-toolbar__search-input"
                      placeholder="Cerca nome, email o telefono"
                      aria-label="Cerca eventi per nome, email o telefono"
                    />
                    <button
                      v-if="eventSearchTerm"
                      type="button"
                      class="event-search__clear"
                      @click="clearEventSearch"
                      aria-label="Svuota ricerca"
                    >
                      <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                  </div>
                  <div class="operator-filter calendari-eventi-toolbar__control calendari-eventi-toolbar__operator">
                    <span class="material-icons operator-filter__icon" aria-hidden="true">person</span>
                    <select
                      v-model="activeOperatorFilter"
                      class="operator-filter__select calendari-eventi-toolbar__operator-select"
                      aria-label="Filtra eventi per operatore"
                    >
                      <option value="">Tutti gli operatori</option>
                      <option
                        v-for="option in operatorOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <button
                      v-if="activeOperatorFilter"
                      type="button"
                      class="operator-filter__clear"
                      title="Azzera filtro operatore"
                      aria-label="Azzera filtro operatore"
                      @click="activeOperatorFilter = ''"
                    >
                      <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                  </div>
                  <div class="calendari-eventi-toolbar__status-area">
                    <div class="status-filter-group">
                      <button
                        v-for="option in statusOptions"
                        :key="option.value"
                        type="button"
                        class="status-filter-chip"
                        :class="{ 'status-filter-chip--active': isStatusFilterActive(option.value) }"
                        :style="{ '--status-color': statusColors[option.value] }"
                        @click="toggleStatusFilter(option.value)"
                        :aria-pressed="isStatusFilterActive(option.value)"
                        :title="option.label"
                        :aria-label="option.label"
                      >
                        <span class="material-icons status-filter-chip__icon">
                          {{ statusIcons[option.value] }}
                        </span>
                      </button>
                    </div>
                    <span class="event-counter" :style="{ borderColor: activeCalendarConfig.accent }">
                      {{ activeEventCount }}
                    </span>
                  </div>
                </div>
              </div>

              <template v-if="showWeekView">
                <div class="week-toolbar-secondary">
                  <div class="week-toolbar-secondary__left">
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToPreviousWeek"
                    >
                      <span class="material-icons">chevron_left</span>
                    </button>
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToNextWeek"
                    >
                      <span class="material-icons">chevron_right</span>
                    </button>
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToCurrentWeek"
                    >
                      Oggi
                    </button>
                  </div>
                  <div class="week-toolbar-secondary__right">
                    <div class="week-view-mode-toggle" role="group" aria-label="Modalità visualizzazione settimana">
                      <button
                        type="button"
                        class="week-view-mode-toggle__btn"
                        :class="{ 'week-view-mode-toggle__btn--active': !isWeekAgendaMode }"
                        @click="setWeekViewMode('grid')"
                      >
                        Griglia
                      </button>
                      <button
                        type="button"
                        class="week-view-mode-toggle__btn"
                        :class="{ 'week-view-mode-toggle__btn--active': isWeekAgendaMode }"
                        @click="setWeekViewMode('agenda')"
                      >
                        Agenda
                      </button>
                    </div>
                    <button
                      type="button"
                      class="time-block-toggle"
                      :class="{ 'time-block-toggle--active': showTimeBlocks }"
                      :aria-pressed="showTimeBlocks"
                      :aria-label="formatTimeBlockToggleLabel(weekViewBlockCount)"
                      @click="toggleTimeBlocksVisibility"
                    >
                      <span class="material-icons time-block-toggle__icon" aria-hidden="true">block</span>
                      <span class="time-block-toggle__label">Blocchi</span>
                      <span class="time-block-toggle__count" aria-hidden="true">{{ weekViewBlockCount }}</span>
                      <span class="time-block-toggle__switch" aria-hidden="true">
                        <span class="time-block-toggle__knob" />
                      </span>
                    </button>
                    <div class="week-zoom-toggle" role="group" aria-label="Zoom verticale calendario">
                      <button
                        v-for="zoom in weekViewZoomOptions"
                        :key="zoom"
                        type="button"
                        class="week-zoom-toggle__btn"
                        :class="{ 'week-zoom-toggle__btn--active': zoom === weekViewZoom }"
                        @click="setWeekViewZoom(zoom)"
                      >
                        {{ formatZoomLabel(zoom) }}
                      </button>
                    </div>
                    <div class="week-toolbar-chip">
                      {{ weekViewEventCount }} {{ weekViewEventCount === 1 ? 'evento' : 'eventi' }}
                    </div>
                  </div>
                </div>

                <div class="week-view-card">
                  <div v-if="weekViewEventCount === 0" class="week-view__empty week-view__empty--inline">
                    {{ !showTimeBlocks && weekViewBlockCount > 0
                      ? 'I blocchi orari sono nascosti. Usa il toggle Blocchi per mostrarli.'
                      : 'Nessun evento programmato in questa settimana.' }}
                  </div>
                  <div v-else-if="!isWeekAgendaMode" class="week-view__grid-shell">
                    <div class="week-view__grid" @scroll.passive="handleWeekGridScroll">
                      <div class="week-view__time-column">
                        <div class="week-view__time-header" aria-hidden="true"></div>
                        <span
                          class="week-view__time-slot"
                          v-for="hour in weekViewHours"
                          :key="hour"
                          :style="weekViewTimeSlotStyle"
                        >
                          {{ formatHourLabel(hour) }}
                        </span>
                      </div>
                      <div
                        v-for="day in weekViewDays"
                        :key="day.iso"
                        class="week-view__day-column"
                      >
                        <div
                          class="week-view__day-header"
                          :class="{ 'week-view__day-header--today': day.isToday }"
                        >
                          <span class="week-view__day-name">{{ day.label }}</span>
                          <span class="week-view__day-number">{{ day.number }}</span>
                        </div>
                        <div class="week-view__day-body" :style="weekViewBodyStyle">
                          <div
                            v-for="event in weekGridEventMap[day.iso] || []"
                            :key="event.id"
                            :data-week-event-id="event.isBlock ? null : event.id"
                            class="week-view__event"
                            :class="{
                              'week-view__event--block': event.isBlock,
                              'week-view__event--selected': isWeekEventSelected(event.id),
                              'week-view__event--grouped': Boolean(event.overlapCount)
                            }"
                            :style="event.style"
                            :title="formatWeekGridEventAriaLabel(event)"
                            :aria-label="event.isBlock ? null : formatWeekGridEventAriaLabel(event)"
                            :aria-haspopup="event.overlapCount ? 'dialog' : null"
                            :aria-hidden="event.isBlock ? 'true' : null"
                            @click.stop="onWeekGridEventClick(event, $event.currentTarget)"
                            :role="event.isBlock ? null : 'button'"
                            :tabindex="event.isBlock ? -1 : 0"
                            @keydown.enter.prevent.stop="onWeekGridEventClick(event, $event.currentTarget)"
                            @keydown.space.prevent.stop="onWeekGridEventClick(event, $event.currentTarget)"
                          >
                            <div class="week-view__event-header">
                              <strong class="week-view__event-title">
                                {{ event.name || (event.isBlock ? 'Blocco orario' : 'Evento senza titolo') }}
                              </strong>
                              <span
                                v-if="event.overlapCount"
                                class="week-view__event-overlap-count"
                                aria-hidden="true"
                              >
                                +{{ event.overlapCount }}
                              </span>
                              <span
                                v-else-if="event.statusIcon && !event.isBlock"
                                class="material-icons week-view__event-status"
                                aria-hidden="true"
                              >
                                {{ event.statusIcon }}
                              </span>
                            </div>
                            <span class="week-view__event-time">{{ event.timeLabel }}</span>
                            <span
                              v-if="event.source?.collaborator && !event.isBlock"
                              class="week-view__event-meta"
                            >
                              {{ event.source.collaborator }}
                            </span>
                          </div>
                          <button
                            v-for="group in weekTimeBlockGroupMap[day.iso] || []"
                            :key="`${group.id}-handle`"
                            type="button"
                            class="time-block-handle"
                            :data-week-event-id="group.events.length === 1 ? group.events[0].id : null"
                            :style="group.style"
                            :aria-label="group.ariaLabel"
                            :title="group.title"
                            @click.stop="openTimeBlockGroup(group, $event.currentTarget)"
                          >
                            <span class="time-block-handle__track" aria-hidden="true" />
                            <span class="time-block-handle__badge" aria-hidden="true">
                              <span class="material-icons">block</span>
                              <span v-if="group.events.length > 1" class="time-block-handle__count">
                                {{ group.events.length }}
                              </span>
                            </span>
                            <span class="time-block-handle__tooltip" aria-hidden="true">
                              {{ group.title }}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="week-agenda">
                    <div
                      v-for="day in weekAgendaDays"
                      :key="day.iso"
                      class="week-agenda__day"
                    >
                      <div
                        class="week-agenda__day-header"
                        :class="{ 'week-agenda__day-header--today': day.isToday }"
                      >
                        <span class="week-agenda__day-name">{{ day.label }}</span>
                        <span class="week-agenda__day-number">{{ day.number }}</span>
                      </div>
                      <ul class="week-agenda__events" v-if="day.events.length">
                        <li
                          v-for="event in day.events"
                          :key="event.id"
                          class="week-agenda__event"
                        >
                          <button
                            type="button"
                            class="week-agenda__event-card"
                            :class="{ 'week-agenda__event-card--block': event.isBlock }"
                            @click="onWeekEventClick(event, $event.currentTarget)"
                            :style="{
                              borderColor: event.accent,
                              backgroundColor: event.accent,
                              color: event.textColor || (event.isBlock ? '#ffffff' : '#0f172a')
                            }"
                          >
                            <div class="week-agenda__event-header">
                              <span class="week-agenda__event-title">
                                {{ event.name || (event.isBlock ? 'Blocco orario' : 'Evento senza titolo') }}
                              </span>
                              <span
                                v-if="event.statusIcon && !event.isBlock"
                                class="material-icons week-agenda__event-status"
                                aria-hidden="true"
                              >
                                {{ event.statusIcon }}
                              </span>
                            </div>
                            <span class="week-agenda__event-time">
                              {{ event.timeLabel }}
                            </span>
                            <span
                              v-if="event.source?.collaborator && !event.isBlock"
                              class="week-agenda__event-meta"
                            >
                              {{ event.source.collaborator }}
                            </span>
                          </button>
                        </li>
                      </ul>
                      <div v-else class="week-agenda__empty">Nessun evento</div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="showDayView">
                <div class="day-toolbar-secondary week-toolbar-secondary">
                  <div class="week-toolbar-secondary__left">
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToPreviousDay"
                    >
                      <span class="material-icons">chevron_left</span>
                    </button>
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToNextDay"
                    >
                      <span class="material-icons">chevron_right</span>
                    </button>
                    <button
                      type="button"
                      class="ghost-button week-nav-button"
                      @click="goToCurrentDay"
                    >
                      Oggi
                    </button>
                  </div>
                  <div class="week-toolbar-secondary__right">
                    <button
                      type="button"
                      class="time-block-toggle"
                      :class="{ 'time-block-toggle--active': showTimeBlocks }"
                      :aria-pressed="showTimeBlocks"
                      :aria-label="formatTimeBlockToggleLabel(dayViewBlockCount)"
                      @click="toggleTimeBlocksVisibility"
                    >
                      <span class="material-icons time-block-toggle__icon" aria-hidden="true">block</span>
                      <span class="time-block-toggle__label">Blocchi</span>
                      <span class="time-block-toggle__count" aria-hidden="true">{{ dayViewBlockCount }}</span>
                      <span class="time-block-toggle__switch" aria-hidden="true">
                        <span class="time-block-toggle__knob" />
                      </span>
                    </button>
                    <div class="week-zoom-toggle" role="group" aria-label="Zoom verticale calendario">
                      <button
                        v-for="zoom in weekViewZoomOptions"
                        :key="zoom"
                        type="button"
                        class="week-zoom-toggle__btn"
                        :class="{ 'week-zoom-toggle__btn--active': zoom === weekViewZoom }"
                        @click="setWeekViewZoom(zoom)"
                      >
                        {{ formatZoomLabel(zoom) }}
                      </button>
                    </div>
                    <div class="week-toolbar-chip">
                      {{ dayViewEventCount }} {{ dayViewEventCount === 1 ? 'evento' : 'eventi' }}
                    </div>
                  </div>
                </div>

                <div class="day-view-card week-view-card">
                  <div v-if="dayViewEventCount === 0" class="week-view__empty week-view__empty--inline">
                    {{ !showTimeBlocks && dayViewBlockCount > 0
                      ? 'I blocchi orari sono nascosti. Usa il toggle Blocchi per mostrarli.'
                      : 'Nessun evento programmato in questa giornata.' }}
                  </div>
                  <div v-else class="day-view__grid-shell">
                    <div class="day-view__grid">
                      <div class="week-view__time-column">
                        <div class="week-view__time-header" aria-hidden="true"></div>
                        <span
                          class="week-view__time-slot"
                          v-for="hour in weekViewHours"
                          :key="hour"
                          :style="weekViewTimeSlotStyle"
                        >
                          {{ formatHourLabel(hour) }}
                        </span>
                      </div>
                      <div class="day-view__day-column">
                        <div
                          class="day-view__day-header"
                          :class="{ 'day-view__day-header--today': dayViewData.isToday }"
                        >
                          <span class="day-view__day-name">{{ dayViewData.label }}</span>
                          <span class="day-view__day-number">{{ dayViewData.number }}</span>
                        </div>
                        <div class="week-view__day-body" :style="weekViewBodyStyle">
                          <div
                            v-for="event in dayViewEvents"
                            :key="event.id"
                            :data-week-event-id="event.isBlock ? null : event.id"
                            class="week-view__event"
                            :class="{
                              'week-view__event--block': event.isBlock,
                              'week-view__event--selected': isWeekEventSelected(event.id)
                            }"
                            :style="event.style"
                            :title="formatWeekEventTooltip(event)"
                            :aria-label="event.isBlock ? null : formatWeekEventTooltip(event)"
                            :aria-hidden="event.isBlock ? 'true' : null"
                            @click.stop="onDayGridEventClick(event, $event.currentTarget)"
                            :role="event.isBlock ? null : 'button'"
                            :tabindex="event.isBlock ? -1 : 0"
                            @keydown.enter.prevent.stop="onDayGridEventClick(event, $event.currentTarget)"
                            @keydown.space.prevent.stop="onDayGridEventClick(event, $event.currentTarget)"
                          >
                            <div class="week-view__event-header">
                              <strong class="week-view__event-title">
                                {{ event.name || (event.isBlock ? 'Blocco orario' : 'Evento senza titolo') }}
                              </strong>
                              <span
                                v-if="event.statusIcon && !event.isBlock"
                                class="material-icons week-view__event-status"
                                aria-hidden="true"
                              >
                                {{ event.statusIcon }}
                              </span>
                            </div>
                            <span class="week-view__event-time">{{ event.timeLabel }}</span>
                            <span
                              v-if="event.source?.collaborator && !event.isBlock"
                              class="week-view__event-meta"
                            >
                              {{ event.source.collaborator }}
                            </span>
                          </div>
                          <button
                            v-for="group in dayTimeBlockGroups"
                            :key="`${group.id}-handle`"
                            type="button"
                            class="time-block-handle"
                            :data-week-event-id="group.events.length === 1 ? group.events[0].id : null"
                            :style="group.style"
                            :aria-label="group.ariaLabel"
                            :title="group.title"
                            @click.stop="openTimeBlockGroup(group, $event.currentTarget)"
                          >
                            <span class="time-block-handle__track" aria-hidden="true" />
                            <span class="time-block-handle__badge" aria-hidden="true">
                              <span class="material-icons">block</span>
                              <span v-if="group.events.length > 1" class="time-block-handle__count">
                                {{ group.events.length }}
                              </span>
                            </span>
                            <span class="time-block-handle__tooltip" aria-hidden="true">
                              {{ group.title }}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <ul v-else-if="activeEvents.length" class="event-items">
                <li v-for="event in activeEvents" :key="event.id" class="event-item">
                  <div v-if="canUpdateStatus(event)" class="status-pill-group">
                    <button
                      v-for="option in statusOptions"
                      :key="option.value"
                      type="button"
                      class="status-pill"
                      :class="{ 'status-pill--active': getEventStatus(event) === option.value }"
                      :style="statusPillStyle(option, getEventStatus(event) === option.value)"
                      :disabled="isStatusUpdating(event)"
                      @click="updateStatus(event, option.value)"
                      :title="getStatusPillTitle(event, option)"
                      :aria-label="getStatusPillTitle(event, option)"
                    >
                      <span class="material-icons status-pill__icon">
                        {{ statusIcons[option.value] }}
                      </span>
                    </button>
                  </div>
                  <div v-if="event.isReminder" class="reminder-pill">
                    <span class="material-icons">notifications_active</span>
                    {{ event.reminderLabel || 'Promemoria commerciale' }}
                  </div>
                  <div v-else-if="event.isBlock" class="block-pill">
                    <span class="material-icons">block</span>
                    Blocco orario
                  </div>
                  <div v-if="!event.isReminder" class="color-picker-inline">
                    <span class="color-picker-inline__label">Colore</span>
                    <div class="color-picker-inline__swatches">
                      <button
                        v-for="color in EVENT_COLOR_PALETTE"
                        :key="color"
                        type="button"
                        class="color-swatch color-swatch--small"
                        :class="{ 'color-swatch--selected': normalizeColorValue(event.color) === color }"
                        :style="{ backgroundColor: color, color: getReadableTextColor(color) }"
                        :aria-label="`Imposta colore ${color}`"
                        @click="updateEventColor(event, color)"
                      >
                        <span v-if="normalizeColorValue(event.color) === color" class="material-icons color-swatch__check">check</span>
                      </button>
                      <button
                        type="button"
                        class="color-swatch color-swatch--small color-swatch--reset"
                        aria-label="Ripristina colore predefinito"
                        @click="updateEventColor(event, '')"
                      >
                        Predefinito
                      </button>
                    </div>
                  </div>

                  <div class="event-main">
                    <div class="event-main__header">
                      <h4>{{ event.name }}</h4>
                      <span
                        v-if="isGlobalListView"
                        class="event-calendar-chip"
                        :style="{ color: getCalendarAccent(event.type) }"
                      >
                        {{ getCalendarLabel(event.type) }}
                      </span>
                      <span
                        v-if="event.isBlock"
                        class="block-chip"
                      >
                        Blocco
                      </span>
                      <span
                        v-if="event.isReminder && event.reminderMeta?.stato"
                        class="reminder-status-chip"
                      >
                        <span class="material-icons">label</span>
                        {{ event.reminderMeta.stato }}
                      </span>
                    </div>
                    <p>
                      {{ formatTimeRange(event.start, event.end) || 'Orario non impostato' }}
                      <span v-if="event.duration"> · {{ event.duration }} minuti</span>
                    </p>
                  </div>

                  <div class="event-meta">
                    <p v-if="isGlobalListView" class="event-meta__row">
                      <strong class="event-meta__label">Data:</strong>
                      <span class="event-meta__value">{{ formatEventDateLabel(event.start) }}</span>
                    </p>
                    <p v-if="event.collaborator && !event.isBlock" class="event-meta__row">
                      <strong class="event-meta__label">Collaboratore:</strong>
                      <span class="event-meta__value">{{ event.collaborator }}</span>
                    </p>
                    <p v-if="event.reminderMeta?.time" class="event-meta__row">
                      <strong class="event-meta__label">Orario richiesto:</strong>
                      <span class="event-meta__value">{{ event.reminderMeta.time }}</span>
                    </p>
                    <p v-if="event.userId && !event.isBlock" class="event-meta__row">
                      <strong class="event-meta__label">U.</strong>
                      <a
                        :href="getUserProfileLink(event.userId)"
                        class="event-user-link event-meta__value"
                        target="_blank"
                        rel="noopener"
                        :title="event.user || 'Profilo utente'"
                      >
                        {{ truncateMetaValue(event.user || 'Profilo utente') }}
                      </a>
                    </p>
                    <p v-else-if="event.user && !event.isBlock" class="event-meta__row">
                      <strong class="event-meta__label">U.</strong>
                      <span
                        class="event-meta__value"
                        :title="event.user"
                      >
                        {{ truncateMetaValue(event.user) }}
                      </span>
                    </p>
                    <p v-if="event.email && !event.isBlock" class="event-meta__row">
                      <strong class="event-meta__label">E.</strong>
                      <span
                        class="event-meta__value"
                        :title="event.email"
                      >
                        {{ truncateMetaValue(event.email) }}
                      </span>
                    </p>
                    <p v-if="getEventContactId(event) || event.email" class="event-meta__row">
                      <strong class="event-meta__label">Contatto:</strong>
                      <a
                        :href="getContactListLink(event)"
                        class="event-user-link event-meta__value"
                        target="_blank"
                        rel="noopener"
                      >
                        Apri in contatti
                      </a>
                    </p>
                    <p v-if="getEventContactId(event)" class="event-meta__row">
                      <strong class="event-meta__label">Ex cliente:</strong>
                      <label class="event-meta__toggle">
                        <input
                          type="checkbox"
                          :checked="event.excliente === true"
                          @change="toggleExClienteFromEvent(event, $event.target.checked)"
                        />
                        <span>{{ event.excliente ? 'Sì' : 'No' }}</span>
                      </label>
                    </p>
                    <p v-else-if="event.email" class="event-meta__row">
                      <strong class="event-meta__label">Ex cliente:</strong>
                      <span class="event-meta__value">{{ formatExClienteValue(event.excliente) }}</span>
                    </p>
                    <p v-if="event.phone && !event.isBlock" class="event-meta__row">
                      <strong class="event-meta__label">Telefono:</strong>
                      <span class="event-meta__value">{{ event.phone }}</span>
                    </p>
                    <p v-if="event.notes" class="event-meta__row">
                      <strong class="event-meta__label">Note:</strong>
                      <span class="event-meta__value">{{ event.notes }}</span>
                    </p>
                    <div class="event-actions" v-if="!event.isReminder">
                      <button
                        type="button"
                        class="event-action-btn"
                        title="Modifica evento"
                        @click="openEditEvent(event)"
                      >
                        <span class="material-icons">edit</span>
                      </button>
                      <button
                        type="button"
                        class="event-action-btn event-action-btn--danger"
                        title="Elimina evento"
                        @click="deleteEvent(event)"
                      >
                        <span class="material-icons">delete</span>
                      </button>
                    </div>
                    <div
                      v-else-if="getEventContactId(event)"
                      class="event-actions"
                    >
                      <button
                        type="button"
                        class="event-action-btn"
                        title="Modifica collaboratore"
                        @click="openReminderOperatorModal(event)"
                      >
                        <span class="material-icons">person</span>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>

              <div v-else-if="isListView" class="empty-state">
                <p v-if="hasActiveSearch">Nessun evento trovato per "{{ eventSearchTerm }}".</p>
                <p v-else-if="isStatusGlobalView">Nessun evento corrisponde ai filtri selezionati.</p>
                <p v-else>Nessun evento programmato per questa giornata.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>

  <transition name="fade">
    <div
      v-if="isGridView && selectedWeekOverlapGroup"
      class="week-event-modal-backdrop"
      @click.self="closeWeekOverlapGroup"
    >
      <article
        ref="weekOverlapDialogRef"
        class="week-event-modal week-overlap-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="week-overlap-modal-heading"
        aria-describedby="week-overlap-modal-description"
        tabindex="-1"
        @keydown="handleWeekOverlapDialogKeydown"
      >
        <button
          type="button"
          class="icon-button week-event-modal__close"
          :aria-label="selectedWeekOverlapGroup.kind === 'blocks' ? 'Chiudi blocchi orari' : 'Chiudi appuntamenti raggruppati'"
          @click="closeWeekOverlapGroup"
        >
          <span class="material-icons">close</span>
        </button>

        <header class="week-event-modal__header week-overlap-modal__header">
          <div class="week-event-modal__title">
            <h4 id="week-overlap-modal-heading">
              {{ selectedWeekOverlapGroup.kind === 'blocks' ? 'Blocchi orari sovrapposti' : 'Appuntamenti raggruppati' }}
            </h4>
          </div>
          <p class="week-event-modal__time">
            {{ selectedWeekOverlapGroup.dateLabel }} · {{ selectedWeekOverlapGroup.timeLabel }}
          </p>
          <p id="week-overlap-modal-description" class="week-overlap-modal__description">
            <template v-if="selectedWeekOverlapGroup.kind === 'blocks'">
              Seleziona uno dei {{ selectedWeekOverlapGroup.eventCount }} blocchi presenti nello stesso intervallo.
            </template>
            <template v-else>
              Questa scheda raggruppa {{ selectedWeekOverlapGroup.hiddenCount }}
              {{ selectedWeekOverlapGroup.hiddenCount === 1 ? 'appuntamento nascosto' : 'appuntamenti nascosti' }} nella griglia.
            </template>
          </p>
        </header>

        <ul class="week-overlap-modal__list">
          <li
            v-for="event in selectedWeekOverlapGroup.events"
            :key="event.id"
            class="week-overlap-modal__item"
          >
            <button
              type="button"
              class="week-overlap-modal__event"
              data-overlap-event
              :style="{ '--overlap-event-accent': getEventAccent(event, getCalendarAccent(event.type)) }"
              @click="openWeekOverlapEvent(event)"
            >
              <span class="week-overlap-modal__event-title">
                {{ event.name || (event.isBlock ? 'Blocco orario' : 'Evento senza titolo') }}
              </span>
              <span class="week-overlap-modal__event-meta">
                {{ formatTimeRange(event.start, event.end) }}
                <template v-if="event.collaborator"> · {{ event.collaborator }}</template>
              </span>
            </button>
          </li>
        </ul>
      </article>
    </div>
  </transition>

  <transition name="fade">
    <div
      v-if="isGridView && selectedWeekEvent"
      class="week-event-modal-backdrop"
      @click.self="closeSelectedWeekEvent"
    >
      <article
        ref="weekEventDetailDialogRef"
        class="week-event-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="week-event-modal-heading"
        tabindex="-1"
        @keydown="handleWeekEventDetailDialogKeydown"
      >
        <button
          type="button"
          class="icon-button week-event-modal__close"
          aria-label="Chiudi dettagli evento"
          @click="closeSelectedWeekEvent"
        >
          <span class="material-icons">close</span>
        </button>

        <div
          v-if="canUpdateStatus(selectedWeekEvent)"
          class="status-pill-group week-event-modal__status"
        >
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': getEventStatus(selectedWeekEvent) === option.value }"
            :style="statusPillStyle(option, getEventStatus(selectedWeekEvent) === option.value)"
            :disabled="isStatusUpdating(selectedWeekEvent)"
            @click="updateStatus(selectedWeekEvent, option.value)"
            :title="getStatusPillTitle(selectedWeekEvent, option)"
            :aria-label="getStatusPillTitle(selectedWeekEvent, option)"
          >
            <span class="material-icons status-pill__icon">
              {{ statusIcons[option.value] }}
            </span>
          </button>
        </div>
        <div v-if="selectedWeekEvent.isReminder" class="reminder-pill week-event-modal__status">
          <span class="material-icons">notifications_active</span>
          {{ selectedWeekEvent.reminderLabel || 'Promemoria commerciale' }}
        </div>
        <div v-else-if="selectedWeekEvent.isBlock" class="block-pill week-event-modal__status">
          <span class="material-icons">block</span>
          Blocco orario
        </div>

        <div v-if="selectedWeekEvent && !selectedWeekEvent.isReminder" class="color-picker-inline color-picker-inline--modal">
          <span class="color-picker-inline__label">Colore</span>
          <div class="color-picker-inline__swatches">
            <button
              v-for="color in EVENT_COLOR_PALETTE"
              :key="color"
              type="button"
              class="color-swatch color-swatch--small"
              :class="{ 'color-swatch--selected': normalizeColorValue(selectedWeekEvent.color) === color }"
              :style="{ backgroundColor: color, color: getReadableTextColor(color) }"
              :aria-label="`Imposta colore ${color}`"
              @click="updateEventColor(selectedWeekEvent, color)"
            >
              <span v-if="normalizeColorValue(selectedWeekEvent.color) === color" class="material-icons color-swatch__check">check</span>
            </button>
            <button
              type="button"
              class="color-swatch color-swatch--small color-swatch--reset"
              aria-label="Ripristina colore predefinito"
              @click="updateEventColor(selectedWeekEvent, '')"
            >
              Predefinito
            </button>
          </div>
        </div>

        <header class="week-event-modal__header">
          <div class="week-event-modal__title">
            <h4 id="week-event-modal-heading">{{ selectedWeekEvent.name }}</h4>
            <span
              v-if="isGlobalListView"
              class="event-calendar-chip"
              :style="{ color: getCalendarAccent(selectedWeekEvent.type) }"
            >
              {{ getCalendarLabel(selectedWeekEvent.type) }}
            </span>
            <span
              v-if="selectedWeekEvent.isBlock"
              class="block-chip"
            >
              Blocco
            </span>
            <span
              v-if="selectedWeekEvent.isReminder && selectedWeekEvent.reminderMeta?.stato"
              class="reminder-status-chip"
            >
              <span class="material-icons">label</span>
              {{ selectedWeekEvent.reminderMeta.stato }}
            </span>
          </div>
          <p class="week-event-modal__time">
            {{ formatTimeRange(selectedWeekEvent.start, selectedWeekEvent.end) || 'Orario non impostato' }}
            <span v-if="selectedWeekEvent.duration"> · {{ selectedWeekEvent.duration }} minuti</span>
          </p>
        </header>

        <div class="event-meta week-event-modal__meta">
          <p class="event-meta__row">
            <strong class="event-meta__label">Data:</strong>
            <span class="event-meta__value">{{ formatEventDateLabel(selectedWeekEvent.start) }}</span>
          </p>
          <p v-if="selectedWeekEvent.collaborator && !selectedWeekEvent.isBlock" class="event-meta__row">
            <strong class="event-meta__label">Collaboratore:</strong>
            <span class="event-meta__value">{{ selectedWeekEvent.collaborator }}</span>
          </p>
          <p v-if="selectedWeekEvent.reminderMeta?.time" class="event-meta__row">
            <strong class="event-meta__label">Orario richiesto:</strong>
            <span class="event-meta__value">{{ selectedWeekEvent.reminderMeta.time }}</span>
          </p>
          <p v-if="selectedWeekEvent.userId && !selectedWeekEvent.isBlock" class="event-meta__row">
            <strong class="event-meta__label">U.</strong>
            <a
              :href="getUserProfileLink(selectedWeekEvent.userId)"
              class="event-user-link event-meta__value"
              target="_blank"
              rel="noopener"
              :title="selectedWeekEvent.user || 'Profilo utente'"
            >
              {{ truncateMetaValue(selectedWeekEvent.user || 'Profilo utente') }}
            </a>
          </p>
          <p v-else-if="selectedWeekEvent.user && !selectedWeekEvent.isBlock" class="event-meta__row">
            <strong class="event-meta__label">U.</strong>
            <span
              class="event-meta__value"
              :title="selectedWeekEvent.user"
            >
              {{ truncateMetaValue(selectedWeekEvent.user) }}
            </span>
          </p>
          <p v-if="selectedWeekEvent.email && !selectedWeekEvent.isBlock" class="event-meta__row">
            <strong class="event-meta__label">E.</strong>
            <span
              class="event-meta__value"
              :title="selectedWeekEvent.email"
            >
              {{ truncateMetaValue(selectedWeekEvent.email) }}
            </span>
          </p>
          <p v-if="getEventContactId(selectedWeekEvent) || selectedWeekEvent.email" class="event-meta__row">
            <strong class="event-meta__label">Contatto:</strong>
            <a
              :href="getContactListLink(selectedWeekEvent)"
              class="event-user-link event-meta__value"
              target="_blank"
              rel="noopener"
            >
              Apri in contatti
            </a>
          </p>
          <p v-if="getEventContactId(selectedWeekEvent)" class="event-meta__row">
            <strong class="event-meta__label">Ex cliente:</strong>
            <label class="event-meta__toggle">
              <input
                type="checkbox"
                :checked="selectedWeekEvent.excliente === true"
                @change="toggleExClienteFromEvent(selectedWeekEvent, $event.target.checked)"
              />
              <span>{{ selectedWeekEvent.excliente ? 'Sì' : 'No' }}</span>
            </label>
          </p>
          <p v-else-if="selectedWeekEvent.email" class="event-meta__row">
            <strong class="event-meta__label">Ex cliente:</strong>
            <span class="event-meta__value">{{ formatExClienteValue(selectedWeekEvent.excliente) }}</span>
          </p>
          <p v-if="selectedWeekEvent.phone && !selectedWeekEvent.isBlock" class="event-meta__row">
            <strong class="event-meta__label">Telefono:</strong>
            <span class="event-meta__value">{{ selectedWeekEvent.phone }}</span>
          </p>
          <p v-if="selectedWeekEvent.notes" class="event-meta__row">
            <strong class="event-meta__label">Note:</strong>
            <span class="event-meta__value">{{ selectedWeekEvent.notes }}</span>
          </p>
          <div class="event-actions week-event-modal__actions" v-if="!selectedWeekEvent.isReminder">
            <button
              type="button"
              class="event-action-btn"
              title="Modifica evento"
              @click="openEditEvent(selectedWeekEvent)"
            >
              <span class="material-icons">edit</span>
            </button>
            <button
              type="button"
              class="event-action-btn event-action-btn--danger"
              title="Elimina evento"
              @click="deleteEvent(selectedWeekEvent)"
            >
              <span class="material-icons">delete</span>
            </button>
          </div>
          <div
            v-else-if="getEventContactId(selectedWeekEvent)"
            class="event-actions week-event-modal__actions"
          >
            <button
              type="button"
              class="event-action-btn"
              title="Modifica collaboratore"
              @click="openReminderOperatorModal(selectedWeekEvent)"
            >
              <span class="material-icons">person</span>
            </button>
          </div>
        </div>
      </article>
    </div>
  </transition>

  <div v-if="showModal" class="modal-backdrop" @click.self="closeEventModal">
    <div class="modal-card">
      <header class="modal-header">
        <h2>
          {{
            isEditing
              ? (eventForm.isBlock ? 'Modifica blocco' : 'Modifica evento')
              : (eventForm.isBlock ? 'Crea nuovo blocco' : 'Crea nuovo evento')
          }}
        </h2>
        <button type="button" class="icon-button" @click="closeEventModal">
          <span class="material-icons">close</span>
        </button>
      </header>

      <form class="modal-form" @submit.prevent="submitEvent">
        <label>
          {{ eventForm.isBlock ? 'Titolo blocco' : 'Nome evento' }}
          <input
            v-model.trim="eventForm.name"
            type="text"
            :placeholder="eventForm.isBlock ? 'Es. Blocco mattutino' : EVENT_TITLE_PLACEHOLDER"
            required
          />
        </label>

        <div class="form-row">
          <label class="form-field">
            {{ eventForm.isBlock ? 'Data e ora inizio blocco' : 'Data e ora' }}
            <input
              v-model="eventForm.startDateTime"
              type="datetime-local"
              required
            />
          </label>

          <label v-if="eventForm.isBlock" class="form-field">
            Data e ora fine blocco
            <input
              v-model="eventForm.endDateTime"
              type="datetime-local"
              required
            />
          </label>

          <label v-else class="form-field">
            Durata
            <select v-model.number="eventForm.duration" required>
              <option v-for="option in durationOptions" :key="option" :value="option">
                {{ option }} minuti
              </option>
            </select>
          </label>
        </div>

        <label v-if="!eventForm.isBlock">
          Collaboratore
          <select v-model="eventForm.collaborator" required>
            <option value="">Seleziona collaboratore</option>
            <option
              v-for="option in collaboratorSelectOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label v-if="!eventForm.isBlock" class="user-field">
          Nome utente
          <div class="user-search">
            <input
              v-model="userSearchTerm"
              type="text"
              placeholder="Cerca utente registrato"
              @input="handleUserSearchInput"
              @focus="handleUserInputFocus"
              @blur="handleUserInputBlur"
            />
            <button
              v-if="userSearchTerm"
              type="button"
              class="user-search__clear"
              title="Svuota campo"
              @mousedown.prevent
              @click="clearSelectedUser"
            >
              <span class="material-icons">close</span>
            </button>
          </div>

          <div
            v-if="userSearchDropdownOpen"
            class="user-suggestions"
            @mousedown.prevent
          >
            <div v-if="isUserSearchLoading" class="user-suggestions__loading">
              Ricerca utenti in corso…
            </div>
            <template v-else>
              <button
                v-for="suggestion in userSuggestions"
                :key="suggestion.id"
                type="button"
                class="user-suggestions__item"
                @click="selectUserSuggestion(suggestion)"
              >
                <span class="user-suggestions__primary">{{ suggestion.display }}</span>
                <span v-if="suggestion.phone" class="user-suggestions__secondary">{{ suggestion.phone }}</span>
              </button>
              <div
                v-if="!userSuggestions.length && userSearchTerm.trim().length >= MIN_USER_SEARCH"
                class="user-suggestions__empty"
              >
                Nessun utente trovato.
              </div>
            </template>
          </div>

          <small class="user-search__hint">Digita almeno {{ MIN_USER_SEARCH }} lettere o parte dell'email per cercare tra gli utenti registrati.</small>
        </label>

        <label>
          Note
          <textarea
            v-model.trim="eventForm.notes"
            rows="3"
            placeholder="Informazioni aggiuntive o promemoria"
          ></textarea>
        </label>

        <label v-if="!eventForm.isBlock">
          Stato evento
          <select v-model="eventForm.status" required>
            <option
              v-for="status in statusOptions"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
        </label>

        <footer class="modal-actions">
          <button type="button" class="secondary-button staff-button-cancel" @click="closeEventModal">
            Annulla
          </button>
          <button type="submit" class="primary-button staff-button-positive">
            {{ isEditing ? 'Aggiorna evento' : 'Salva evento' }}
          </button>
        </footer>
      </form>
    </div>
  </div>

  <div
    v-if="showReminderOperatorModal"
    class="modal-backdrop modal-backdrop--overlay"
    @click.self="closeReminderOperatorModal"
  >
    <div class="modal-card">
      <header class="modal-header">
        <h2>Collaboratore promemoria</h2>
        <button type="button" class="icon-button" @click="closeReminderOperatorModal">
          <span class="material-icons">close</span>
        </button>
      </header>

      <form class="modal-form" @submit.prevent="submitReminderOperator">
        <label>
          Collaboratore
          <select v-model="reminderOperatorValue">
            <option value="">Nessun operatore</option>
            <option
              v-for="option in reminderCollaboratorOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <footer class="modal-actions">
          <button type="button" class="secondary-button staff-button-cancel" @click="closeReminderOperatorModal">
            Annulla
          </button>
          <button type="submit" class="primary-button staff-button-positive">
            Salva
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Calendar } from 'v-calendar';
import 'v-calendar/style.css';
import SidebarComponent from '@/components/SidebarComponent.vue';
import {
  addDoc,
  collection,
  doc,
  getFirestore,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
  where,
  startAt,
  endAt,
  Timestamp,
  updateDoc,
  deleteField,
  deleteDoc
} from 'firebase/firestore';

const calendars = [
  { id: 'commercial', label: 'Commerciale', accent: '#db2777' },
  { id: 'giampaolo', label: 'Giampaolo', accent: '#0d9488' },
  { id: 'technical', label: 'Tecnico', accent: '#2563eb' },
  { id: 'nutritional', label: 'Nutrizionale', accent: '#16a34a' }
];

const statusOptions = [
  { value: 'da_confermare', label: 'Da confermare', color: '#f59e0b' },
  { value: 'da_riprogrammare', label: 'Da riprogrammare', color: '#db2777' },
  { value: 'confermato', label: 'Confermato', color: '#2563eb' },
  { value: 'fatto', label: 'Fatto', color: '#16a34a' }
];

const statusColors = statusOptions.reduce((acc, item) => {
  acc[item.value] = item.color;
  return acc;
}, {});
const statusLabelMap = statusOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const statusIcons = {
  da_confermare: 'hourglass_empty',
  da_riprogrammare: 'autorenew',
  confermato: 'done',
  fatto: 'check_circle'
};

const OPERATOR_OPTIONS = [
  {
    value: 'gaetano',
    label: 'Gaetano',
    aliases: ['gae', 'gaatano', 'gaet', 'gaeta', 'gaetano maniscalco', 'gaetano@fusco.fit', 'gaetano.m@fusco.fit', 'getano']
  },
  {
    value: 'paolom',
    label: 'Paolo M.',
    aliases: ['paolo m', 'paolo m.', 'paolom', 'paolomarchini', 'paolo marchini', 'paolomarchini@fusco.fit', 'paolo']
  },
  { value: 'paolor', label: 'Paolo R.', aliases: ['paolo r', 'paolo r.', 'paolor', 'Paoloramunno'] },
  { value: 'lucac', label: 'Luca C.', aliases: ['luca c', 'luca c.', 'lucac', 'luca'] },
    { value: 'alessiof', label: 'Alessio F.', aliases: ['alessio f', 'alessio f.', 'alessiof', 'alessiofantini'] },
  { value: 'roberta', label: 'Roberta', aliases: ['roby', 'robertacurto@fusco.fit'] },
  { value: 'pasquale', label: 'Pasquale' },
  { value: 'salvatore', label: 'Salvatore', aliases: ['salvo'] },
    { value: 'veronica', label: 'Veronica' },
  { value: 'svetlana', label: 'Svetlana' },

  { value: 'sede', label: 'Sede' }
];
const HIDDEN_OPERATOR_OPTIONS = [
  { value: 'chiara', label: 'Chiara' },
  { value: 'noemi', label: 'Noemi' },
  { value: 'marika', label: 'Marika' },
  { value: 'sonia', label: 'Sonia' },
  { value: 'eleonora', label: 'Eleonora' },
  { value: 'elisabetta', label: 'Elisabetta' },
  { value: 'giorgia', label: 'Giorgia' },
  { value: 'michela', label: 'Michela' },
  { value: 'lucad', label: 'Luca D.', aliases: ['luca d', 'luca d.', 'lucad', 'lucadelfino', 'luca delfino'] },
  { value: 'stefania', label: 'Stefania' },
  { value: 'giuseppe', label: 'Giuseppe', aliases: ['peppe'] },
  { value: 'alessandro', label: 'Alessandro', aliases: ['ale'] },
  { value: 'mirko', label: 'Mirko' },
  { value: 'michele', label: 'Michele' },
  { value: 'rayen', label: 'Rayen' },
  { value: 'sede', label: 'Sede' },
  { value: 'valentina', label: 'Valentina' },
  { value: 'mario rossi', label: 'Mario Rossi' },
  { value: 'valerio test', label: 'Valerio Test' },

];
const NO_OPERATOR_VALUE = 'nooperatore';
const NO_OPERATOR_LABEL = 'NO OPERATORE';
const COLLABORATOR_FIELD_PRIORITY = ['operatore2', 'operatore3', 'operatore1', 'operatore', 'collaborator'];
const REMINDER_OPERATOR_FIELDS = {
  first: 'operatore2',
  second: 'operatore3'
};
const REMINDER_CALENDAR_STATUS_FIELDS = Object.freeze({
  first: 'calendarStatus2',
  second: 'calendarStatus3'
});
const COLLABORATOR_COLOR_PALETTE = [
  '#1d4ed8',
  '#0ea5e9',
  '#06b6d4',
  '#0f766e',
  '#14b8a6',
  '#10b981',
  '#22c55e',
  '#16a34a',
  '#84cc16',
  '#eab308',
  '#f59e0b',
  '#f97316',
  '#ea580c',
  '#ef4444',
  '#dc2626',
  '#f43f5e',
  '#db2777',
  '#be123c',
  '#c026d3',
  '#9333ea',
  '#6d28d9',
  '#4f46e5',
  '#4338ca',
  '#0f172a',
  '#334155',
  '#64748b',
  '#0d9488',
  '#15803d',
  '#b45309',
  '#7c2d12'
];
const CONTACT_OPERATOR_VALUE_MAP = {
  gaetano: 'Gaetano',
  paolom: 'Paolomarchini',
  paolor: 'Paoloramunno',
  lucac: 'Luca',
  lucad: 'lucadelfino',
  roberta: 'Roberta',
  pasquale: 'pasquale',
  stefania: 'stefania',
  salvatore: 'Salvatore',
  giuseppe: 'giuseppe',
  alessandro: 'alessandro',
  alessiof: 'alessiofantini',
  mirko: 'mirko',
  michele: 'michele',
  veronica: 'veronica',
  svetlana: 'svetlana',
  sede: 'sede'
};

const durationOptions = [15, 20, 30, 45, 60, 90, 120];
const DEFAULT_EVENT_DURATION_MINUTES = 20;
const EVENT_COLOR_PALETTE = [
  '#0ea5e9',
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#f97316',
  '#a855f7',
  '#22d3ee',
  '#facc15',
  '#fb7185'
];
const COMMERCIAL_CALENDAR_ID = 'commercial';
const COMMERCIAL_COLLABORATOR_COLOR_OVERRIDES = {
  paolom: '#0ea5e9',
  salvatore: '#eab308',
  alessiof: '#a855f7'
};
const COMMERCIAL_REMINDER_DURATION = 30;
const COMMERCIAL_REMINDER_SOURCES = Object.freeze([
  { field: 'dataricontatto2', type: 'first' },
  { field: 'dataricontatto3', type: 'second' }
]);
const REALTIME_NOTIFICATION_DURATION = 5000;
const REALTIME_NOTIFICATION_GAP = 240;
const MAX_REALTIME_NOTIFICATION_QUEUE = 3;
const MAX_DAY_DOTS = 10;
const MAX_VISIBLE_EVENT_DOTS = MAX_DAY_DOTS - 1;
const USER_SEARCH_FETCH_LIMIT = 2000;
const MAX_USER_SUGGESTIONS = 40;
const META_FIELD_MAX_LENGTH = 42;
const ALL_STATUS_VALUES = statusOptions.map((option) => option.value);
const EVENT_TITLE_PLACEHOLDER = "Inserisci il titolo dell'evento";
const WEEK_VIEW_BASE_MINUTE_HEIGHT = 1;
const WEEK_VIEW_MIN_EVENT_HEIGHT = 36;
const WEEK_VIEW_COLUMN_GAP = 2;
const WEEK_VIEW_COLUMN_INSET = 2;
const WEEK_VIEW_MAX_VISIBLE_APPOINTMENT_COLUMNS = 2;
const DAY_VIEW_COLUMN_GAP = 6;
const WEEK_VIEW_EVENT_Z_BASE = 10;
const WEEK_VIEW_BLOCK_Z_BASE = 1;
const WEEK_VIEW_START_HOUR = 7;
const WEEK_VIEW_START_MINUTE = WEEK_VIEW_START_HOUR * 60;
const WEEK_VIEW_END_MINUTE = 24 * 60;
const WEEK_VIEW_VISIBLE_MINUTES = WEEK_VIEW_END_MINUTE - WEEK_VIEW_START_MINUTE;
const WEEK_VIEW_VISIBLE_HOURS = Math.ceil(WEEK_VIEW_VISIBLE_MINUTES / 60);
const WEEK_VIEW_ZOOM_STORAGE_KEY = 'calendars.weekViewZoom';
const TIME_BLOCK_VISIBILITY_STORAGE_KEY = 'calendars.showTimeBlocks';
const WEEK_VIEW_ZOOM_OPTIONS = [1, 1.5, 2, 2.5, 3];
const DEFAULT_WEEK_VIEW_ZOOM = 1.5;
const MOBILE_VIEWPORT_QUERY = '(max-width: 768px)';
const OPERATOR_LOOKUP = buildOperatorLookup([...OPERATOR_OPTIONS, ...HIDDEN_OPERATOR_OPTIONS]);
const COLLABORATOR_COLOR_MAP = buildCollaboratorColorMap([...OPERATOR_OPTIONS, ...HIDDEN_OPERATOR_OPTIONS]);
const DYNAMIC_COLLABORATOR_COLORS = new Map();
const USED_COLLABORATOR_COLORS = new Set(COLLABORATOR_COLOR_MAP.values());

const db = getFirestore();
const calendarCollectionRef = collection(db, 'calendar');
const contactsCollectionRef = collection(db, 'contatti');
let unsubscribeFromCalendar = null;
let unsubscribeFromCommercialReminders = [];
let hasHydratedCalendarEvents = false;
const commercialReminderEventSources = new Map(
  COMMERCIAL_REMINDER_SOURCES.map(({ field }) => [field, new Map()])
);
const commercialReminderReadyFields = new Set();

const calendarEvents = ref([]);
const commercialReminderEvents = ref([]);
const realtimeNotification = ref(null);
const realtimeNotificationQueue = [];
const realtimeNotificationPauseReasons = new Set();
let realtimeNotificationTimer = null;
let realtimeNotificationGapTimer = null;
let realtimeNotificationSequence = 0;
const events = computed(() => [
  ...calendarEvents.value,
  ...commercialReminderEvents.value
]);
const calendarMap = calendars.reduce((acc, calendar) => {
  acc[calendar.id] = calendar;
  return acc;
}, {});
const activeCalendarKey = ref(calendars[0].id);
const showTimeBlocks = ref(true);

const calendarState = reactive(
  calendars.reduce((acc, calendar) => {
    const today = startOfDay(new Date());
    acc[calendar.id] = {
      focus: today,
      selectedDate: today,
      searchTerm: '',
      operatorFilter: '',
      statusFilters: [...ALL_STATUS_VALUES]
    };
    return acc;
  }, {})
);

const minDate = startOfDay(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
const maxReminderDate = startOfDay(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));

function mapCalendarDocument(docSnap) {
  const data = docSnap.data();
  const startDate = data.start?.toDate ? data.start.toDate() : new Date(data.start);
  const endDateRaw = data.end?.toDate ? data.end.toDate() : new Date(data.end || startDate);
  const durationMinutes =
    typeof data.duration === 'number'
      ? data.duration
      : Math.max(Math.round((endDateRaw - startDate) / 60000), 15);
  const collaboratorRaw = resolveCollaboratorValue(data);
  const collaboratorLabel = getOperatorLabel(collaboratorRaw);

  return {
    id: docSnap.id,
    type: data.calendarType || data.type || calendars[0].id,
    name: data.name || '',
    start: startDate.toISOString(),
    end: endDateRaw.toISOString(),
    duration: durationMinutes,
    collaborator: collaboratorLabel,
    operators: collectOperatorValues([
      data.collaborator,
      data.operatore,
      data.operatore1,
      data.operatore2,
      data.operatore3
    ]),
    user: data.user || '',
    userId: data.userId || '',
    email: data.email || '',
    excliente: normalizeExClienteValue(data.excliente),
    phone: data.phone || '',
    notes: data.notes || '',
    status: normalizeStatus(data.status),
    color: normalizeColorValue(data.color || data.colore),
    isBlock: Boolean(data.isBlock),
    isReminder: Boolean(data.isReminder),
    reminderLabel: data.reminderLabel || '',
    reminderMeta: data.reminderMeta || null
  };
}

onMounted(() => {
  const storedZoom = getStoredWeekViewZoom();
  if (storedZoom !== null) {
    weekViewZoom.value = storedZoom;
  }
  const storedTimeBlockVisibility = getStoredTimeBlockVisibility();
  if (storedTimeBlockVisibility !== null) {
    showTimeBlocks.value = storedTimeBlockVisibility;
  }

  const eventsQuery = query(calendarCollectionRef, orderBy('start', 'asc'));
  unsubscribeFromCalendar = onSnapshot(
    eventsQuery,
    { includeMetadataChanges: true },
    (snapshot) => {
      const fetchedEvents = snapshot.docs.map(mapCalendarDocument);
      const addedEvents = hasHydratedCalendarEvents && !snapshot.metadata.fromCache
        ? snapshot.docChanges()
          .filter((change) => change.type === 'added' && !change.doc.metadata?.hasPendingWrites)
          .map((change) => mapCalendarDocument(change.doc))
        : [];

      calendarEvents.value = fetchedEvents;
      if (!snapshot.metadata.fromCache) {
        hasHydratedCalendarEvents = true;
      }
      notifyRealtimeAppointments(addedEvents);
    },
    (error) => {
      console.error('Errore durante il recupero degli eventi calendario:', error);
    }
  );

  startCommercialReminderRealtime();
});

onUnmounted(() => {
  if (unsubscribeFromCalendar) {
    unsubscribeFromCalendar();
  }
  stopCommercialReminderRealtime();
  clearRealtimeNotifications();
  if (userSearchTimeoutId) {
    clearTimeout(userSearchTimeoutId);
    userSearchTimeoutId = null;
  }
});

const calendarAttributes = computed(() => {
  const attributeMap = {};

  calendars.forEach((calendar) => {
    const attributes = [];
    const eventsByDate = new Map();

    events.value
      .filter((event) => event.type === calendar.id)
      .forEach((event) => {
        const eventDate = startOfDay(new Date(event.start));
        if (Number.isNaN(eventDate.getTime())) {
          return;
        }

        const dateKey = eventDate.toISOString();
        const baseColor = getEventAccent(event, calendar.accent);
        const popoverLabel = event.isReminder
          ? [event.name, event.reminderLabel].filter(Boolean).join(' • ')
          : event.isBlock
            ? `${event.name || 'Blocco orario'} • ${formatEventDateLabel(event.start)}`
          : event.name;

        if (!eventsByDate.has(dateKey)) {
          eventsByDate.set(dateKey, {
            date: eventDate,
            items: []
          });
        }

        eventsByDate.get(dateKey).items.push({
          event,
          baseColor,
          popoverLabel
        });
      });

    eventsByDate.forEach(({ date, items }, dateKey) => {
      const sortedItems = items.slice().sort((a, b) => {
        return new Date(a.event.start) - new Date(b.event.start);
      });

      const hasOverflow = sortedItems.length > MAX_DAY_DOTS;
      const visibleItems = hasOverflow
        ? sortedItems.slice(0, MAX_VISIBLE_EVENT_DOTS)
        : sortedItems.slice(0, MAX_DAY_DOTS);

      visibleItems.forEach(({ event, baseColor, popoverLabel }) => {
        attributes.push({
          key: event.id,
          dot: {
            color: baseColor,
            style: {
              borderWidth: '2px',
              borderColor: baseColor,
              backgroundColor: baseColor
            }
          },
          dates: date,
          popover: {
            label: popoverLabel,
            visibility: 'hover'
          }
        });
      });

      if (hasOverflow) {
        const overflowCount = sortedItems.length - MAX_VISIBLE_EVENT_DOTS;
        attributes.push({
          key: `${calendar.id}-${dateKey}-overflow`,
          dot: {
            class: 'vc-dot--more',
            style: {
              color: calendar.accent
            }
          },
          dates: date,
          popover: {
            label: `+${overflowCount} eventi`,
            visibility: 'hover'
          }
        });
      }
    });

    const selectedDate = calendarState[calendar.id].selectedDate;
    if (selectedDate) {
      attributes.push({
        key: `${calendar.id}-selected`,
        dates: selectedDate,
        highlight: {
          color: calendar.accent,
          fillMode: 'outline'
        }
      });
    }

    attributeMap[calendar.id] = attributes;
  });

  return attributeMap;
});

const activeCalendarConfig = computed(() => {
  return calendars.find((calendar) => calendar.id === activeCalendarKey.value) || calendars[0];
});

const activeCalendarAttributes = computed(() => {
  return calendarAttributes.value[activeCalendarKey.value] || [];
});

const activeSelectedDate = computed(() => {
  const state = calendarState[activeCalendarKey.value];
  return state ? state.selectedDate : null;
});

const activeCalendarInitialPage = computed(() => {
  const state = calendarState[activeCalendarKey.value];
  const focusDate = state?.focus instanceof Date && !Number.isNaN(state.focus.getTime())
    ? state.focus
    : startOfDay(new Date());

  return {
    month: focusDate.getMonth() + 1,
    year: focusDate.getFullYear()
  };
});

const eventSearchTerm = computed({
  get() {
    const state = calendarState[activeCalendarKey.value];
    return state ? state.searchTerm : '';
  },
  set(value) {
    const state = calendarState[activeCalendarKey.value];
    if (state) {
      state.searchTerm = value;
    }
  }
});
const activeOperatorFilter = computed({
  get() {
    const state = calendarState[activeCalendarKey.value];
    return state ? state.operatorFilter : '';
  },
  set(value) {
    const state = calendarState[activeCalendarKey.value];
    if (state) {
      state.operatorFilter = normalizeOperatorValue(value);
    }
  }
});
const normalizedEventSearchTerm = computed(() => normalizeSearchValue(eventSearchTerm.value));
const hasActiveSearch = computed(() => normalizedEventSearchTerm.value.length > 0);
const normalizedActiveOperatorFilter = computed(() => normalizeOperatorValue(activeOperatorFilter.value));
const activeStatusFilters = computed(() => {
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return [...ALL_STATUS_VALUES];
  }

  const filters = state.statusFilters || [];
  if (!filters.length) {
    return [];
  }

  return [...new Set(filters.map((value) => normalizeStatus(value)))];
});

const filteredCalendarEvents = computed(() => {
  const calendarId = activeCalendarKey.value;
  const filters = activeStatusFilters.value;
  const baseEvents = events.value.filter((event) => event.type === calendarId);
  const operatorFilter = normalizedActiveOperatorFilter.value;

  if (!filters.length) {
    return [];
  }

  const statusFilteredEvents =
    filters.length === ALL_STATUS_VALUES.length
      ? baseEvents
      : baseEvents.filter((event) => {
        if (event.isBlock) {
          return true;
        }
        return filters.includes(getEventStatus(event));
      });

  if (!operatorFilter) {
    return statusFilteredEvents;
  }

  return statusFilteredEvents.filter((event) => {
    if (event.isBlock) {
      return false;
    }
    return eventMatchesOperator(event, operatorFilter);
  });
});

const isStatusFilterRestrictive = computed(() => {
  const filters = activeStatusFilters.value;
  if (!filters.length) {
    return true;
  }

  return filters.length < ALL_STATUS_VALUES.length;
});

const isStatusGlobalView = computed(() => !hasActiveSearch.value && isStatusFilterRestrictive.value);
const isGlobalListView = computed(() => hasActiveSearch.value || isStatusGlobalView.value);

const activeStatusLabels = computed(() => {
  if (!activeStatusFilters.value.length || activeStatusFilters.value.length === ALL_STATUS_VALUES.length) {
    return '';
  }
  return activeStatusFilters.value
    .map((value) => statusLabelMap[value] || value)
    .join(', ');
});
const operatorOptions = computed(() => {
  const hiddenSet = hiddenOperatorSet.value;

  const baseOptions = OPERATOR_OPTIONS
    .filter((option) => !hiddenSet.has(normalizeOperatorValue(option.value)))
    .map((option) => ({
      value: option.value,
      label: option.label
    }));

  const seen = new Set(baseOptions.map((option) => normalizeOperatorValue(option.value)));
  seen.add(NO_OPERATOR_VALUE);
  const extras = new Map();

  events.value
    .filter((event) => event.type === activeCalendarKey.value)
    .forEach((event) => {
      getEventOperators(event).forEach((operator) => {
        const normalized = normalizeOperatorValue(operator);
        if (!normalized || seen.has(normalized) || extras.has(normalized) || hiddenSet.has(normalized)) {
          return;
        }
        extras.set(normalized, getOperatorLabel(operator));
      });
    });

  const extraOptions = Array.from(extras.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));

  return [
    { value: NO_OPERATOR_VALUE, label: NO_OPERATOR_LABEL },
    ...baseOptions,
    ...extraOptions
  ];
});
const collaboratorSelectOptions = computed(() => {
  const options = operatorOptions.value.filter((option) => option.value !== NO_OPERATOR_VALUE);
  const current = normalizeOperatorValue(eventForm.collaborator);
  if (current && !options.some((option) => option.value === current)) {
    options.push({
      value: current,
      label: getOperatorLabel(eventForm.collaborator) || eventForm.collaborator
    });
  }
  return options;
});
const reminderCollaboratorOptions = computed(() => {
  const options = operatorOptions.value.filter((option) => option.value !== NO_OPERATOR_VALUE);
  const current = normalizeOperatorValue(reminderOperatorValue.value);
  if (current && !options.some((option) => option.value === current)) {
    options.push({
      value: current,
      label: getOperatorLabel(reminderOperatorValue.value) || reminderOperatorValue.value
    });
  }
  return options;
});
const activeOperatorLabel = computed(() => {
  if (!normalizedActiveOperatorFilter.value) {
    return '';
  }
  const match = operatorOptions.value.find((option) => option.value === normalizedActiveOperatorFilter.value);
  return match?.label || '';
});

// Lazy in-memory index: typing and changing filters reuse the normalized values.
const eventSearchIndex = computed(() => new Map(
  events.value.map((event) => [event, buildEventSearchEntry(event)])
));

const searchResults = computed(() => {
  if (!hasActiveSearch.value) {
    return [];
  }

  const term = normalizedEventSearchTerm.value;
  const phoneDigits = getSearchPhoneDigits(term);
  const index = eventSearchIndex.value;
  return filteredCalendarEvents.value
    .filter((event) => {
      const entry = index.get(event);
      return entry && (
        entry.texts.some((text) => text.includes(term)) ||
        (phoneDigits && entry.phones.some((phone) => phone.includes(phoneDigits)))
      );
    })
    .sort((a, b) => new Date(a.start) - new Date(b.start));
});

const activeEvents = computed(() => {
  if (hasActiveSearch.value) {
    return searchResults.value;
  }

  if (isStatusGlobalView.value) {
    return filteredCalendarEvents.value.slice().sort((a, b) => new Date(a.start) - new Date(b.start));
  }

  const selectedDate = activeSelectedDate.value;
  if (!selectedDate) {
    return [];
  }

  return filteredCalendarEvents.value
    .filter((event) => isSameDay(new Date(event.start), selectedDate))
    .sort((a, b) => new Date(a.start) - new Date(b.start));
});

const weekStartDate = computed(() => startOfWeek(activeSelectedDate.value || new Date()));
const weekEndDate = computed(() => addDays(weekStartDate.value, 7));
const weekViewDays = computed(() => {
  const today = new Date();
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekStartDate.value, index);
    const startIso = startOfDay(date).toISOString();
    return {
      date,
      iso: startIso,
      label: formatWeekDayLabel(date),
      number: date.getDate(),
      isToday: isSameDay(date, today)
    };
  });
});
const weekViewHours = Array.from({ length: WEEK_VIEW_VISIBLE_HOURS }, (_, idx) => idx + WEEK_VIEW_START_HOUR);
const weekViewRangeLabel = computed(() => formatWeekRangeLabel(weekStartDate.value, addDays(weekStartDate.value, 6)));
const weekViewBodyStyle = computed(() => {
  const minuteHeight = weekViewMinuteHeight.value;
  const totalPixels = WEEK_VIEW_VISIBLE_MINUTES * minuteHeight;
  return {
    height: `${totalPixels}px`,
    minHeight: `${totalPixels}px`,
    backgroundSize: `100% ${60 * minuteHeight}px`
  };
});
const weekViewTimeSlotStyle = computed(() => {
  const minuteHeight = weekViewMinuteHeight.value;
  const slotHeight = 60 * minuteHeight;
  return {
    height: `${slotHeight}px`,
    minHeight: `${slotHeight}px`
  };
});
const weekViewBlockCount = computed(() => {
  const start = weekStartDate.value;
  const end = weekEndDate.value;
  return filteredCalendarEvents.value.filter((event) => {
    if (!event.isBlock) {
      return false;
    }
    const eventStart = new Date(event.start);
    return !Number.isNaN(eventStart.getTime()) && eventStart >= start && eventStart < end;
  }).length;
});

const weekViewEventMap = computed(() => {
  const minuteHeight = weekViewMinuteHeight.value;
  const map = {};
  const start = weekStartDate.value;
  const end = weekEndDate.value;
  filteredCalendarEvents.value.forEach((event) => {
    if (event.isBlock && !showTimeBlocks.value) {
      return;
    }
    const eventStart = new Date(event.start);
    if (Number.isNaN(eventStart.getTime())) {
      return;
    }
    if (eventStart < start || eventStart >= end) {
      return;
    }
    const bucket = startOfDay(eventStart).toISOString();
    if (!map[bucket]) {
      map[bucket] = [];
    }
    const rendered = buildWeekViewEvent(event, minuteHeight);
    if (rendered) {
      map[bucket].push(rendered);
    }
  });

  Object.values(map).forEach((events) => {
    events.sort(compareWeekLayoutEvents);
  });

  return map;
});

const weekGridEventMap = computed(() => {
  return Object.entries(weekViewEventMap.value).reduce((map, [dayIso, events]) => {
    const layoutEvents = events.map((event) => ({
      ...event,
      style: { ...event.style },
      overlapCount: 0,
      overlapEvents: []
    }));
    map[dayIso] = layoutWeekDayEvents(layoutEvents, {
      aggregateOverflow: true,
      maxVisibleColumns: WEEK_VIEW_MAX_VISIBLE_APPOINTMENT_COLUMNS,
      columnGap: WEEK_VIEW_COLUMN_GAP
    });
    return map;
  }, {});
});
const weekTimeBlockGroupMap = computed(() => {
  return Object.entries(weekGridEventMap.value).reduce((map, [dayIso, events]) => {
    map[dayIso] = buildTimeBlockInteractionGroups(events.filter((event) => event.isBlock));
    return map;
  }, {});
});

const dayViewDate = computed(() => {
  const selected = activeSelectedDate.value || new Date();
  return startOfDay(selected);
});
const dayViewData = computed(() => {
  const date = dayViewDate.value;
  if (!date) {
    return {
      date: null,
      label: '',
      number: '',
      isToday: false
    };
  }
  const today = new Date();
  return {
    date,
    label: formatWeekDayLabel(date),
    number: date.getDate(),
    isToday: isSameDay(date, today)
  };
});
const dayViewBlockCount = computed(() => {
  const start = dayViewDate.value;
  if (!start) {
    return 0;
  }
  const end = addDays(start, 1);
  return filteredCalendarEvents.value.filter((event) => {
    if (!event.isBlock) {
      return false;
    }
    const eventStart = new Date(event.start);
    return !Number.isNaN(eventStart.getTime()) && eventStart >= start && eventStart < end;
  }).length;
});
const dayViewEvents = computed(() => {
  const minuteHeight = weekViewMinuteHeight.value;
  const start = dayViewDate.value;
  if (!start) {
    return [];
  }
  const end = addDays(start, 1);
  const eventsForDay = [];

  filteredCalendarEvents.value.forEach((event) => {
    if (event.isBlock && !showTimeBlocks.value) {
      return;
    }
    const eventStart = new Date(event.start);
    if (Number.isNaN(eventStart.getTime())) {
      return;
    }
    if (eventStart < start || eventStart >= end) {
      return;
    }
    const rendered = buildWeekViewEvent(event, minuteHeight);
    if (rendered) {
      eventsForDay.push(rendered);
    }
  });

  eventsForDay.sort(compareWeekLayoutEvents);
  return layoutWeekDayEvents(eventsForDay, {
    aggregateOverflow: false,
    columnGap: DAY_VIEW_COLUMN_GAP,
  });
});
const dayTimeBlockGroups = computed(() => {
  return buildTimeBlockInteractionGroups(dayViewEvents.value.filter((event) => event.isBlock));
});
const dayViewEventCount = computed(() => dayViewEvents.value.length);
const dayViewEventMap = computed(() => {
  if (!showDayView.value || !dayViewDate.value) {
    return {};
  }
  return {
    [dayViewDate.value.toISOString()]: dayViewEvents.value
  };
});
const gridEventMap = computed(() => {
  if (showWeekView.value) {
    return weekViewEventMap.value;
  }
  if (showDayView.value) {
    return dayViewEventMap.value;
  }
  return {};
});

const weekViewEventCount = computed(() => {
  return Object.values(weekViewEventMap.value).reduce((total, events) => total + events.length, 0);
});

const activeEventCount = computed(() => {
  if (showWeekView.value) {
    return weekViewEventCount.value;
  }
  if (showDayView.value) {
    return dayViewEventCount.value;
  }
  return activeEvents.value.length;
});

const weekAgendaDays = computed(() => {
  return weekViewDays.value.map((day) => {
    const events = (weekViewEventMap.value[day.iso] || []).slice().sort((a, b) => a.startMinutes - b.startMinutes);
    return {
      ...day,
      events
    };
  });
});

const eventToolbarTitle = computed(() => {
  if (showWeekView.value) {
    return weekViewRangeLabel.value;
  }
  if (activeSelectedDate.value) {
    return formatToolbarDate(activeSelectedDate.value);
  }
  return 'Eventi';
});

const eventToolbarHint = computed(() => {
  const hints = [];
  if (hasActiveSearch.value && eventSearchTerm.value) {
    hints.push(`Ricerca: "${eventSearchTerm.value}"`);
  }
  if (isStatusGlobalView.value && activeStatusLabels.value) {
    hints.push(`Filtri: ${activeStatusLabels.value}`);
  }
  if (activeOperatorLabel.value) {
    hints.push(`Operatore: ${activeOperatorLabel.value}`);
  }
  return hints.join(' • ');
});

const showModal = ref(false);
const showReminderOperatorModal = ref(false);
const calendarViewMode = ref('list');
const showWeekView = computed(() => calendarViewMode.value === 'week');
const showDayView = computed(() => calendarViewMode.value === 'day');
const isListView = computed(() => calendarViewMode.value === 'list');
const isGridView = computed(() => showWeekView.value || showDayView.value);
const modalCalendarId = ref(null);
const isEditing = ref(false);
const editingEventId = ref(null);
const reminderOperatorEvent = ref(null);
const reminderOperatorValue = ref('');
const userSearchTerm = ref('');
const userSuggestions = ref([]);
const isUserSearchLoading = ref(false);
const userSearchDropdownOpen = ref(false);
const usersCollectionRef = collection(db, 'users');
const MIN_USER_SEARCH = 3;
let userSearchTimeoutId = null;
const weekViewMode = ref('grid');
const isWeekAgendaMode = computed(() => weekViewMode.value === 'agenda');
const weekViewZoom = ref(DEFAULT_WEEK_VIEW_ZOOM);
const weekViewZoomOptions = WEEK_VIEW_ZOOM_OPTIONS;
const weekViewMinuteHeight = computed(() => WEEK_VIEW_BASE_MINUTE_HEIGHT * weekViewZoom.value);
const selectedWeekEvent = ref(null);
const selectedWeekOverlapGroup = ref(null);
const weekOverlapDialogRef = ref(null);
const weekEventDetailDialogRef = ref(null);
let weekOverlapReturnFocusElement = null;
let weekEventDetailReturnFocusElement = null;
const updatingStatusKeys = ref(new Set());
const hiddenOperatorSet = computed(() => buildHiddenOperatorSet());
const isMobileViewport = ref(false);
let mobileViewportMediaQuery = null;

const eventForm = reactive({
  name: '',
  startDateTime: '',
  endDateTime: '',
  duration: DEFAULT_EVENT_DURATION_MINUTES,
  collaborator: '',
  user: '',
  userId: '',
  email: '',
  phone: '',
  notes: '',
  status: statusOptions[0].value,
  color: '',
  isBlock: false
});

function getUserProfileLink(userId) {
  if (!userId) {
    return null;
  }
  return `#/user/${userId}/anagrafica`;
}

function getContactListLink(event) {
  if (!event) {
    return '#/contatti-list';
  }

  const params = [];
  const email = (event.email || '').toString().trim().toLowerCase();
  const contactId = getEventContactId(event);

  if (email) {
    params.push(`email=${encodeURIComponent(email)}`);
  }
  if (contactId) {
    params.push(`contactId=${encodeURIComponent(contactId)}`);
  }

  if (!params.length) {
    return '#/contatti-list';
  }

  return `#/contatti-list?${params.join('&')}`;
}

function isWeekEventSelected(id) {
  if (!id) {
    return false;
  }
  return selectedWeekEvent.value?.id === id;
}

function isDomElement(value) {
  return typeof HTMLElement !== 'undefined' && value instanceof HTMLElement;
}

function focusWeekEventDetailDialog() {
  nextTick(() => {
    const closeButton = weekEventDetailDialogRef.value?.querySelector('.week-event-modal__close');
    (closeButton || weekEventDetailDialogRef.value)?.focus();
  });
}

function onWeekEventClick(weekEvent, triggerElement = null) {
  if (!weekEvent) {
    return;
  }
  const targetId = weekEvent.source?.id || weekEvent.id;
  if (!targetId) {
    return;
  }
  if (selectedWeekEvent.value?.id === targetId) {
    closeSelectedWeekEvent();
    return;
  }
  weekOverlapReturnFocusElement = null;
  weekEventDetailReturnFocusElement = isDomElement(triggerElement) ? triggerElement : null;
  selectedWeekEvent.value = weekEvent.source || null;
  focusWeekEventDetailDialog();
}

function formatWeekGridEventAriaLabel(weekEvent) {
  const baseLabel = formatWeekEventTooltip(weekEvent);
  if (!weekEvent?.overlapCount) {
    return baseLabel;
  }
  const overlapLabel = weekEvent.overlapCount === 1
    ? '1 altro appuntamento sovrapposto'
    : `${weekEvent.overlapCount} altri appuntamenti sovrapposti`;
  return `${baseLabel}. ${overlapLabel}. Attiva per aprire l'elenco.`;
}

function onWeekGridEventClick(weekEvent, triggerElement = null) {
  if (weekEvent?.isBlock) {
    return;
  }
  if (weekEvent?.overlapCount && weekEvent.overlapEvents?.length) {
    openWeekOverlapGroup(weekEvent, triggerElement);
    return;
  }
  onWeekEventClick(weekEvent, triggerElement);
}

function onDayGridEventClick(weekEvent, triggerElement = null) {
  if (weekEvent?.isBlock) {
    return;
  }
  onWeekEventClick(weekEvent, triggerElement);
}

function openTimeBlockGroup(group, triggerElement = null) {
  const blockEvents = (group?.events || []).filter((event) => event?.isBlock);
  if (!blockEvents.length) {
    return;
  }
  if (blockEvents.length === 1) {
    onWeekEventClick(blockEvents[0], triggerElement);
    return;
  }

  const sourceEvents = blockEvents
    .map((event) => event.source)
    .filter(Boolean)
    .sort((eventA, eventB) => new Date(eventA.start) - new Date(eventB.start));
  if (!sourceEvents.length) {
    return;
  }

  selectedWeekEvent.value = null;
  weekEventDetailReturnFocusElement = null;
  if (isDomElement(triggerElement)) {
    weekOverlapReturnFocusElement = triggerElement;
  }
  selectedWeekOverlapGroup.value = {
    id: `${group.id}-blocks`,
    kind: 'blocks',
    dateLabel: formatEventDateLabel(sourceEvents[0].start),
    timeLabel: group.timeLabel,
    hiddenCount: 0,
    eventCount: sourceEvents.length,
    events: sourceEvents
  };

  nextTick(() => {
    const firstEventButton = weekOverlapDialogRef.value?.querySelector('[data-overlap-event]');
    (firstEventButton || weekOverlapDialogRef.value)?.focus();
  });
}

function openWeekOverlapGroup(weekEvent, triggerElement = null) {
  const events = (weekEvent?.overlapEvents || [])
    .filter(Boolean)
    .slice()
    .sort((eventA, eventB) => new Date(eventA.start) - new Date(eventB.start));
  if (!events.length) {
    onWeekEventClick(weekEvent, triggerElement);
    return;
  }

  const firstEvent = events[0];
  const lastEvent = events.reduce((latest, event) => (
    new Date(event.end) > new Date(latest.end) ? event : latest
  ), firstEvent);

  selectedWeekEvent.value = null;
  weekEventDetailReturnFocusElement = null;
  if (isDomElement(triggerElement)) {
    weekOverlapReturnFocusElement = triggerElement;
  }
  selectedWeekOverlapGroup.value = {
    id: `${weekEvent.id}-overlap`,
    kind: 'appointments',
    dateLabel: formatEventDateLabel(firstEvent.start),
    timeLabel: formatTimeRange(firstEvent.start, lastEvent.end),
    hiddenCount: weekEvent.overlapCount,
    events
  };

  nextTick(() => {
    const firstEventButton = weekOverlapDialogRef.value?.querySelector('[data-overlap-event]');
    (firstEventButton || weekOverlapDialogRef.value)?.focus();
  });
}

function restoreWeekOverlapTriggerFocus() {
  const returnTarget = weekOverlapReturnFocusElement;
  weekOverlapReturnFocusElement = null;
  if (!returnTarget) {
    return;
  }
  nextTick(() => {
    if (typeof document !== 'undefined' && document.contains(returnTarget)) {
      returnTarget.focus();
    }
  });
}

function closeWeekOverlapGroup(restoreFocus = true) {
  selectedWeekOverlapGroup.value = null;
  if (restoreFocus !== false) {
    restoreWeekOverlapTriggerFocus();
  }
}

function openWeekOverlapEvent(event) {
  const returnFocusElement = weekOverlapReturnFocusElement;
  weekOverlapReturnFocusElement = null;
  closeWeekOverlapGroup(false);
  onWeekEventClick({ id: event?.id, source: event }, returnFocusElement);
}

function closeSelectedWeekEvent() {
  selectedWeekEvent.value = null;
  const returnTarget = weekEventDetailReturnFocusElement;
  weekEventDetailReturnFocusElement = null;
  if (!returnTarget) {
    return;
  }
  nextTick(() => {
    if (typeof document !== 'undefined' && document.contains(returnTarget)) {
      returnTarget.focus();
    }
  });
}

function trapDialogFocus(event, dialog) {
  if (event.key !== 'Tab') {
    return;
  }
  if (!dialog) {
    return;
  }
  const focusableElements = Array.from(dialog.querySelectorAll([
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ')));
  if (!focusableElements.length) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
}

function handleWeekOverlapDialogKeydown(event) {
  trapDialogFocus(event, weekOverlapDialogRef.value);
}

function handleWeekEventDetailDialogKeydown(event) {
  trapDialogFocus(event, weekEventDetailDialogRef.value);
}

function syncMobileViewport(isMobile) {
  const nextValue = Boolean(isMobile);
  isMobileViewport.value = nextValue;
  if (nextValue && showWeekView.value && weekViewMode.value !== 'agenda') {
    weekViewMode.value = 'agenda';
  }
}

function handleMobileViewportChange(event) {
  syncMobileViewport(event.matches);
}

function setWeekViewMode(mode) {
  if (weekViewMode.value === mode) {
    return;
  }
  weekViewMode.value = mode;
  if (mode !== 'grid') {
    selectedWeekEvent.value = null;
    selectedWeekOverlapGroup.value = null;
    weekOverlapReturnFocusElement = null;
    weekEventDetailReturnFocusElement = null;
  }
}

function setWeekViewZoom(value) {
  const normalized = normalizeWeekViewZoom(value);
  if (normalized === null || normalized === weekViewZoom.value) {
    return;
  }
  weekViewZoom.value = normalized;
}

function formatTimeBlockToggleLabel(count = 0) {
  const normalizedCount = Number.isFinite(Number(count)) ? Number(count) : 0;
  const itemLabel = normalizedCount === 1 ? 'blocco orario' : 'blocchi orari';
  return `${showTimeBlocks.value ? 'Nascondi' : 'Mostra'} ${normalizedCount} ${itemLabel}`;
}

function toggleTimeBlocksVisibility() {
  showTimeBlocks.value = !showTimeBlocks.value;
  if (showTimeBlocks.value) {
    return;
  }
  if (selectedWeekEvent.value?.isBlock) {
    closeSelectedWeekEvent();
  }
  if (selectedWeekOverlapGroup.value?.kind === 'blocks') {
    closeWeekOverlapGroup();
  }
}

function handleWeekGridScroll(event) {
  const grid = event?.currentTarget;
  if (!grid?.classList) {
    return;
  }

  const isCompact = grid.classList.contains('week-view__grid--scrolled');
  if (!isCompact && grid.scrollTop > 52) {
    grid.classList.add('week-view__grid--scrolled');
  } else if (isCompact && grid.scrollTop <= 4) {
    grid.classList.remove('week-view__grid--scrolled');
  }
}

function handleGlobalKeydown(event) {
  if (event.key !== 'Escape') {
    return;
  }

  if (showReminderOperatorModal.value) {
    closeReminderOperatorModal();
    event.stopPropagation();
    return;
  }

  if (selectedWeekOverlapGroup.value) {
    closeWeekOverlapGroup();
    event.stopPropagation();
    return;
  }

  if (selectedWeekEvent.value) {
    closeSelectedWeekEvent();
    event.stopPropagation();
    return;
  }

  if (showModal.value) {
    closeEventModal();
    event.stopPropagation();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown);
});

onMounted(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }

  mobileViewportMediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
  syncMobileViewport(mobileViewportMediaQuery.matches);

  if (typeof mobileViewportMediaQuery.addEventListener === 'function') {
    mobileViewportMediaQuery.addEventListener('change', handleMobileViewportChange);
    return;
  }

  if (typeof mobileViewportMediaQuery.addListener === 'function') {
    mobileViewportMediaQuery.addListener(handleMobileViewportChange);
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
});

onUnmounted(() => {
  if (!mobileViewportMediaQuery) {
    return;
  }

  if (typeof mobileViewportMediaQuery.removeEventListener === 'function') {
    mobileViewportMediaQuery.removeEventListener('change', handleMobileViewportChange);
    return;
  }

  if (typeof mobileViewportMediaQuery.removeListener === 'function') {
    mobileViewportMediaQuery.removeListener(handleMobileViewportChange);
  }
});

watch(calendarViewMode, (mode) => {
  if (mode !== 'week') {
    weekViewMode.value = 'grid';
  }
  if (mode === 'list') {
    selectedWeekEvent.value = null;
    weekEventDetailReturnFocusElement = null;
  }
  if (mode !== 'week') {
    selectedWeekOverlapGroup.value = null;
    weekOverlapReturnFocusElement = null;
  }
});

watch(weekViewMode, (mode) => {
  if (mode === 'grid' || !showWeekView.value) {
    return;
  }
  selectedWeekEvent.value = null;
  selectedWeekOverlapGroup.value = null;
  weekOverlapReturnFocusElement = null;
  weekEventDetailReturnFocusElement = null;
});

watch(weekGridEventMap, () => {
  if (selectedWeekOverlapGroup.value) {
    closeWeekOverlapGroup();
  }
});

watch(weekViewZoom, (value, previous) => {
  if (value === previous) {
    return;
  }
  persistWeekViewZoom(value);
});

watch(showTimeBlocks, (value, previous) => {
  if (value === previous) {
    return;
  }
  persistTimeBlockVisibility(value);
});

watch([gridEventMap, isGridView], ([map], isGrid) => {
  if (!isGrid || !selectedWeekEvent.value) {
    return;
  }

  let matchedSource = null;
  Object.values(map).some((events) =>
    events.some((event) => {
      if (event.source?.id === selectedWeekEvent.value.id) {
        matchedSource = event.source;
        return true;
      }
      return false;
    })
  );

  if (!matchedSource) {
    closeSelectedWeekEvent();
    return;
  }

  if (matchedSource && matchedSource !== selectedWeekEvent.value) {
    selectedWeekEvent.value = matchedSource;
  }
});

function sanitizeManualUserValue(rawValue) {
  if (!rawValue) {
    return '';
  }

  const normalized = rawValue
    .replace(/[·•]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) {
    return '';
  }

  const tokens = normalized
    .split(/[,;]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean);

  if (!tokens.length) {
    return '';
  }

  const capitalizeToken = (token) =>
    token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();

  if (tokens.length === 1) {
    return capitalizeToken(tokens[0]);
  }

  return `${capitalizeToken(tokens[0])} ${capitalizeToken(tokens[1])}`;
}

function openEventModal(calendarId) {
  modalCalendarId.value = calendarId;
  const selectedDate = calendarState[calendarId].selectedDate;
  const defaultDate = selectedDate ? new Date(selectedDate) : new Date();
  const now = new Date();
  defaultDate.setHours(now.getHours(), now.getMinutes(), 0, 0);

  isEditing.value = false;
  editingEventId.value = null;
  eventForm.name = '';
  eventForm.startDateTime = formatForInput(defaultDate);
  eventForm.endDateTime = '';
  eventForm.duration = DEFAULT_EVENT_DURATION_MINUTES;
  eventForm.collaborator = '';
  eventForm.user = '';
  eventForm.userId = '';
  eventForm.email = '';
  eventForm.phone = '';
  eventForm.notes = '';
  eventForm.status = statusOptions[0].value;
  eventForm.color = '';
  eventForm.isBlock = false;
  userSearchTerm.value = '';
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;

  showModal.value = true;
}

function openBlockModal(calendarId) {
  modalCalendarId.value = calendarId;
  const defaultDate = calendarState[calendarId].selectedDate || new Date();

  isEditing.value = false;
  editingEventId.value = null;
  eventForm.name = 'Blocco orario';
  const startInput = formatForInput(defaultDate);
  const defaultEnd = addMinutes(defaultDate, DEFAULT_EVENT_DURATION_MINUTES);
  eventForm.startDateTime = startInput;
  eventForm.endDateTime = formatForInput(defaultEnd);
  eventForm.duration = DEFAULT_EVENT_DURATION_MINUTES;
  eventForm.collaborator = '';
  eventForm.user = '';
  eventForm.userId = '';
  eventForm.email = '';
  eventForm.phone = '';
  eventForm.notes = '';
  eventForm.status = statusOptions[0].value;
  eventForm.color = '';
  eventForm.isBlock = true;
  userSearchTerm.value = '';
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;

  showModal.value = true;
}

function openEditEvent(event) {
  if (event?.isReminder) {
    return;
  }

  modalCalendarId.value = event.type;
  isEditing.value = true;
  editingEventId.value = event.id;

  eventForm.name = event.name;
  eventForm.startDateTime = formatForInput(event.start);
  eventForm.endDateTime = event.isBlock ? formatForInput(event.end) : '';
  eventForm.duration = event.duration;
  const resolvedOperatorValue = getOperatorValue(event.collaborator) || normalizeOperatorValue(event.collaborator);
  eventForm.collaborator = resolvedOperatorValue;
  eventForm.user = event.user || '';
  eventForm.userId = event.userId || '';
  eventForm.email = event.email || '';
  eventForm.phone = event.phone || '';
  eventForm.notes = event.notes || '';
  eventForm.status = normalizeStatus(event.status);
  eventForm.color = normalizeColorValue(event.color);
  eventForm.isBlock = Boolean(event.isBlock);

  userSearchTerm.value = event.isBlock ? '' : (event.user || '');
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;

  const eventDate = startOfDay(new Date(event.start));
  calendarState[event.type].selectedDate = eventDate;
  calendarState[event.type].focus = eventDate;
  activeCalendarKey.value = event.type;

  selectedWeekEvent.value = null;
  weekOverlapReturnFocusElement = null;
  weekEventDetailReturnFocusElement = null;
  showModal.value = true;
}

function openReminderOperatorModal(event) {
  if (!event?.isReminder) {
    return;
  }
  if (!getEventContactId(event)) {
    return;
  }
  reminderOperatorEvent.value = event;
  const resolved = getOperatorValue(event.collaborator) || normalizeOperatorValue(event.collaborator);
  reminderOperatorValue.value = resolved;
  showReminderOperatorModal.value = true;
}

function closeReminderOperatorModal() {
  showReminderOperatorModal.value = false;
  reminderOperatorEvent.value = null;
  reminderOperatorValue.value = '';
}

async function submitReminderOperator() {
  const event = reminderOperatorEvent.value;
  if (!event) {
    return;
  }
  const contactId = getEventContactId(event);
  if (!contactId) {
    return;
  }

  const field = getReminderOperatorField(event);
  const nextValue = getContactOperatorValue(reminderOperatorValue.value);

  try {
    await updateDoc(doc(contactsCollectionRef, contactId), { [field]: nextValue });
    closeReminderOperatorModal();
  } catch (error) {
    console.error('Errore durante l\'aggiornamento del collaboratore promemoria:', error);
  }
}

function collectCommercialReminderEvents() {
  return COMMERCIAL_REMINDER_SOURCES
    .flatMap(({ field }) => Array.from(commercialReminderEventSources.get(field)?.values() || []))
    .sort((eventA, eventB) => new Date(eventA.start) - new Date(eventB.start));
}

function handleCommercialReminderSnapshot(source, snapshot) {
  const sourceEvents = commercialReminderEventSources.get(source.field);
  if (!sourceEvents) {
    return;
  }

  const wasSourceHydrated = commercialReminderReadyFields.has(source.field);
  const previousEventIds = new Set(commercialReminderEvents.value.map((event) => event.id));
  const remotelyChangedContacts = new Set();

  snapshot.docChanges().forEach((change) => {
    const contactId = change.doc.id;
    const eventId = `${contactId}-reminder-${source.type}`;

    if (!change.doc.metadata?.hasPendingWrites) {
      remotelyChangedContacts.add(contactId);
    }

    if (change.type === 'removed') {
      sourceEvents.delete(eventId);
      return;
    }

    const contact = { ...(change.doc.data() || {}), uid: contactId };
    const reminderEvent = buildCommercialReminderEvents(contact)
      .find((event) => event.reminderMeta?.type === source.type);

    if (reminderEvent) {
      sourceEvents.set(eventId, reminderEvent);
    } else {
      sourceEvents.delete(eventId);
    }
  });

  if (!snapshot.metadata.fromCache) {
    commercialReminderReadyFields.add(source.field);
  }
  const nextEvents = collectCommercialReminderEvents();
  commercialReminderEvents.value = nextEvents;

  if (snapshot.metadata.fromCache || !wasSourceHydrated || !remotelyChangedContacts.size) {
    return;
  }

  const addedEvents = nextEvents.filter((event) => (
    !previousEventIds.has(event.id) &&
    remotelyChangedContacts.has(getEventContactId(event))
  ));
  notifyRealtimeAppointments(addedEvents);
}

function stopCommercialReminderRealtime() {
  unsubscribeFromCommercialReminders.forEach((unsubscribe) => unsubscribe());
  unsubscribeFromCommercialReminders = [];
}

function startCommercialReminderRealtime() {
  stopCommercialReminderRealtime();
  commercialReminderReadyFields.clear();
  commercialReminderEventSources.forEach((sourceEvents) => sourceEvents.clear());
  commercialReminderEvents.value = [];

  const startRange = new Date(minDate);
  const endRange = new Date(maxReminderDate);
  endRange.setHours(23, 59, 59, 999);
  const startTs = Timestamp.fromDate(startRange);
  const endTs = Timestamp.fromDate(endRange);

  unsubscribeFromCommercialReminders = COMMERCIAL_REMINDER_SOURCES.map((source) => {
    const rangeQuery = query(
      contactsCollectionRef,
      orderBy(source.field),
      where(source.field, '>=', startTs),
      where(source.field, '<=', endTs)
    );

    return onSnapshot(
      rangeQuery,
      { includeMetadataChanges: true },
      (snapshot) => handleCommercialReminderSnapshot(source, snapshot),
      (error) => {
        commercialReminderReadyFields.add(source.field);
        console.error(`Errore listener promemoria commerciale (${source.field}):`, error);
      }
    );
  });
}

async function deleteEvent(event) {
  if (event?.isReminder) {
    return;
  }

  const confirmDelete = window.confirm('Eliminare definitivamente questo evento?');
  if (!confirmDelete) {
    return;
  }

  try {
    await deleteDoc(doc(calendarCollectionRef, event.id));
    if (selectedWeekEvent.value?.id === event.id) {
      closeSelectedWeekEvent();
    }
  } catch (error) {
    console.error('Errore durante l\'eliminazione dell\'evento:', error);
  }
}

function closeEventModal() {
  showModal.value = false;
  modalCalendarId.value = null;
  isEditing.value = false;
  editingEventId.value = null;
  eventForm.startDateTime = '';
  eventForm.endDateTime = '';
  eventForm.userId = '';
  eventForm.email = '';
  eventForm.phone = '';
  eventForm.color = '';
  eventForm.isBlock = false;
  userSearchTerm.value = '';
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;
}

async function submitEvent() {
  if (!modalCalendarId.value) {
    return;
  }

  const startDate = eventForm.startDateTime ? new Date(eventForm.startDateTime) : null;
  if (!startDate || Number.isNaN(startDate.getTime())) {
    return;
  }

  const isBlockEvent = Boolean(eventForm.isBlock);
  const isLinkedUser = Boolean(eventForm.userId);
  let durationMinutes;
  let endDate;
  const collaboratorLabel = getOperatorLabel(eventForm.collaborator);

  if (isBlockEvent) {
    endDate = eventForm.endDateTime ? new Date(eventForm.endDateTime) : null;
    if (!endDate || Number.isNaN(endDate.getTime())) {
      window.alert('Imposta una data e ora di fine blocco valide.');
      return;
    }
    if (endDate <= startDate) {
      window.alert('La fine del blocco deve essere successiva all\'inizio.');
      return;
    }
    durationMinutes = Math.max(Math.round((endDate - startDate) / 60000), 5);
  } else {
    durationMinutes = Number(eventForm.duration) || DEFAULT_EVENT_DURATION_MINUTES;
    endDate = new Date(startDate.getTime() + durationMinutes * 60000);
  }

  const userDisplayValue = (() => {
    if (isBlockEvent) {
      return '';
    }
    if (isLinkedUser) {
      return eventForm.user.trim();
    }
    const sanitized = sanitizeManualUserValue(eventForm.user);
    return sanitized || eventForm.user.trim();
  })();

  if (!isBlockEvent) {
    if (!isLinkedUser) {
      eventForm.user = userDisplayValue;
    }
  } else {
    eventForm.user = '';
    eventForm.userId = '';
    eventForm.email = '';
    eventForm.phone = '';
  }

  const payload = {
    calendarType: modalCalendarId.value,
    name: eventForm.name.trim(),
    start: Timestamp.fromDate(startDate),
    end: Timestamp.fromDate(endDate),
    duration: durationMinutes,
    collaborator: isBlockEvent ? '' : collaboratorLabel,
    user: userDisplayValue,
    userId: eventForm.userId || '',
    email: isBlockEvent ? '' : (eventForm.email || '').trim().toLowerCase(),
    phone: isBlockEvent ? '' : (eventForm.phone || '').trim(),
    notes: eventForm.notes.trim(),
    status: normalizeStatus(eventForm.status),
    color: normalizeColorValue(eventForm.color),
    isBlock: isBlockEvent,
    updatedAt: Timestamp.now()
  };

  try {
    if (isEditing.value && editingEventId.value) {
      await updateDoc(doc(calendarCollectionRef, editingEventId.value), payload);
    } else {
      await addDoc(calendarCollectionRef, {
        ...payload,
        createdAt: Timestamp.now()
      });
    }

    const normalizedStart = startOfDay(startDate);
    calendarState[modalCalendarId.value].focus = normalizedStart;
    calendarState[modalCalendarId.value].selectedDate = normalizedStart;
    activeCalendarKey.value = modalCalendarId.value;

    closeEventModal();
  } catch (error) {
    console.error('Errore durante il salvataggio dell\'evento calendario:', error);
  }
}

function onDaySelect(calendarId, dayPayload) {
  const selectedDate = startOfDay(dayPayload.date);
  calendarState[calendarId].selectedDate = selectedDate;
  calendarState[calendarId].focus = selectedDate;
}

function setActiveCalendar(calendarId) {
  activeCalendarKey.value = calendarId;

  const state = calendarState[calendarId];
  if (!state.selectedDate) {
    const today = startOfDay(new Date());
    state.selectedDate = today;
    state.focus = today;
  }
}

function clearEventSearch() {
  eventSearchTerm.value = '';
}

function getCalendarLabel(calendarId) {
  return calendarMap[calendarId]?.label || 'Calendario';
}

function getCalendarAccent(calendarId) {
  return calendarMap[calendarId]?.accent || '#2563eb';
}

function getRealtimeNotificationKey(event) {
  if (!event?.id) {
    return '';
  }
  return `${event.type || 'calendar'}:${event.id}:${event.start || ''}`;
}

function scheduleRealtimeNotificationDismiss() {
  if (realtimeNotificationTimer) {
    clearTimeout(realtimeNotificationTimer);
  }
  realtimeNotificationTimer = setTimeout(() => {
    dismissRealtimeNotification();
  }, REALTIME_NOTIFICATION_DURATION);
}

function showNextRealtimeNotification() {
  if (
    realtimeNotification.value ||
    realtimeNotificationGapTimer ||
    !realtimeNotificationQueue.length
  ) {
    return;
  }
  realtimeNotification.value = realtimeNotificationQueue.shift();
  if (!realtimeNotificationPauseReasons.size) {
    scheduleRealtimeNotificationDismiss();
  }
}

function dismissRealtimeNotification() {
  if (realtimeNotificationTimer) {
    clearTimeout(realtimeNotificationTimer);
    realtimeNotificationTimer = null;
  }
  if (realtimeNotificationGapTimer) {
    clearTimeout(realtimeNotificationGapTimer);
  }
  realtimeNotificationPauseReasons.clear();
  realtimeNotification.value = null;
  realtimeNotificationGapTimer = setTimeout(() => {
    realtimeNotificationGapTimer = null;
    showNextRealtimeNotification();
  }, REALTIME_NOTIFICATION_GAP);
}

function pauseRealtimeNotification(reason) {
  realtimeNotificationPauseReasons.add(reason);
  if (realtimeNotificationTimer) {
    clearTimeout(realtimeNotificationTimer);
    realtimeNotificationTimer = null;
  }
}

function resumeRealtimeNotification(reason) {
  realtimeNotificationPauseReasons.delete(reason);
  if (
    realtimeNotification.value &&
    !realtimeNotificationPauseReasons.size &&
    !realtimeNotificationTimer
  ) {
    scheduleRealtimeNotificationDismiss();
  }
}

function clearRealtimeNotifications() {
  if (realtimeNotificationTimer) {
    clearTimeout(realtimeNotificationTimer);
    realtimeNotificationTimer = null;
  }
  if (realtimeNotificationGapTimer) {
    clearTimeout(realtimeNotificationGapTimer);
    realtimeNotificationGapTimer = null;
  }
  realtimeNotificationPauseReasons.clear();
  realtimeNotificationQueue.splice(0, realtimeNotificationQueue.length);
  realtimeNotification.value = null;
}

function getRealtimeAppointmentName(event) {
  if (event?.isBlock) {
    return event.name || 'Blocco orario';
  }
  if (event?.isReminder) {
    return event.name || event.user || event.email || 'Contatto senza nome';
  }
  return event?.user || event?.email || event?.name || 'Utente non indicato';
}

function enqueueRealtimeNotification(notification) {
  if (realtimeNotificationQueue.length < MAX_REALTIME_NOTIFICATION_QUEUE) {
    realtimeNotificationQueue.push(notification);
  } else {
    const lastIndex = realtimeNotificationQueue.length - 1;
    const previousOverflow = realtimeNotificationQueue[lastIndex];
    const itemCount = (previousOverflow.itemCount || 1) + (notification.itemCount || 1);
    realtimeNotificationQueue[lastIndex] = {
      id: notification.id,
      title: `${itemCount} aggiornamenti in attesa`,
      name: 'Altri appuntamenti ricevuti',
      meta: 'Il calendario è già aggiornato in tempo reale',
      accent: '#0f172a',
      itemCount
    };
  }
  showNextRealtimeNotification();
}

function notifyRealtimeAppointments(appointments = []) {
  const uniqueAppointments = Array.from(
    new Map(
      appointments
        .map((event) => [getRealtimeNotificationKey(event), event])
        .filter(([key]) => Boolean(key))
    ).values()
  ).sort((eventA, eventB) => new Date(eventA.start) - new Date(eventB.start));

  if (!uniqueAppointments.length) {
    return;
  }

  const firstEvent = uniqueAppointments[0];
  const calendarLabels = [...new Set(
    uniqueAppointments.map((event) => getCalendarLabel(event.type))
  )];
  const allBlocks = uniqueAppointments.every((event) => event.isBlock);
  const allAppointments = uniqueAppointments.every((event) => !event.isBlock);
  const previewNames = uniqueAppointments
    .slice(0, 2)
    .map((event) => getRealtimeAppointmentName(event));
  const remainingCount = uniqueAppointments.length - previewNames.length;

  let title;
  let name;
  let meta;

  if (uniqueAppointments.length === 1) {
    title = `${firstEvent.isBlock ? 'Nuovo blocco' : 'Nuovo appuntamento'} · ${getCalendarLabel(firstEvent.type)}`;
    name = getRealtimeAppointmentName(firstEvent);
    meta = [
      !firstEvent.isBlock && !firstEvent.isReminder && firstEvent.name !== name
        ? firstEvent.name
        : firstEvent.reminderLabel,
      formatEventDateLabel(firstEvent.start),
      formatTimeRange(firstEvent.start, firstEvent.end),
      firstEvent.collaborator
    ].filter(Boolean).join(' · ');
  } else {
    const itemLabel = allBlocks
      ? 'nuovi blocchi'
      : (allAppointments ? 'nuovi appuntamenti' : 'nuovi elementi');
    title = `${uniqueAppointments.length} ${itemLabel}`;
    name = `${previewNames.join(' · ')}${remainingCount > 0 ? ` · +${remainingCount}` : ''}`;
    meta = `${calendarLabels.join(' · ')} · aggiornamento in tempo reale`;
  }

  realtimeNotificationSequence += 1;
  enqueueRealtimeNotification({
    id: realtimeNotificationSequence,
    title,
    name,
    meta,
    accent: calendarLabels.length === 1 ? getCalendarAccent(firstEvent.type) : '#0f172a',
    itemCount: uniqueAppointments.length,
    actionLabel: firstEvent.isBlock ? 'Apri blocco' : 'Apri appuntamento',
    target: uniqueAppointments.length === 1
      ? {
        eventId: firstEvent.id,
        calendarId: firstEvent.type,
        start: firstEvent.start
      }
      : null
  });
}

async function openRealtimeNotificationTarget() {
  const target = realtimeNotification.value?.target;
  if (!target?.eventId || !target.calendarId) {
    return;
  }

  const targetEvent = events.value.find((event) => (
    String(event.id) === String(target.eventId) && event.type === target.calendarId
  ));
  if (!targetEvent) {
    dismissRealtimeNotification();
    return;
  }

  const eventDate = startOfDay(new Date(targetEvent.start));
  const state = calendarState[targetEvent.type];
  if (!state || Number.isNaN(eventDate.getTime())) {
    dismissRealtimeNotification();
    return;
  }

  activeCalendarKey.value = targetEvent.type;
  state.searchTerm = '';
  state.operatorFilter = '';
  state.statusFilters = [...ALL_STATUS_VALUES];
  state.focus = eventDate;
  state.selectedDate = eventDate;

  selectedWeekEvent.value = null;
  selectedWeekOverlapGroup.value = null;
  weekOverlapReturnFocusElement = null;
  weekEventDetailReturnFocusElement = null;
  weekViewMode.value = 'grid';
  calendarViewMode.value = 'day';
  dismissRealtimeNotification();

  await nextTick();
  const renderedEvent = dayViewEvents.value.find((event) => event.source?.id === targetEvent.id);
  const dayGrid = typeof document !== 'undefined'
    ? document.querySelector('.day-view__grid')
    : null;
  if (renderedEvent && dayGrid) {
    dayGrid.scrollTop = Math.max(renderedEvent.topPx - dayGrid.clientHeight * 0.2, 0);
  }

  const triggerElement = typeof document !== 'undefined'
    ? Array.from(document.querySelectorAll('[data-week-event-id]')).find(
      (element) => element.getAttribute('data-week-event-id') === String(targetEvent.id)
    ) || null
    : null;
  onWeekEventClick({ id: targetEvent.id, source: targetEvent }, triggerElement);
}

function toggleStatusFilter(statusValue) {
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return;
  }

  const normalizedValue = normalizeStatus(statusValue);
  const currentFilters = Array.isArray(state.statusFilters)
    ? [...new Set(state.statusFilters.map((value) => normalizeStatus(value)))]
    : [...ALL_STATUS_VALUES];
  const hasAllStatuses = currentFilters.length === ALL_STATUS_VALUES.length;

  // Con tutti gli stati visibili, il primo clic applica davvero quel filtro.
  if (hasAllStatuses) {
    state.statusFilters = [normalizedValue];
    return;
  }

  const hasValue = currentFilters.includes(normalizedValue);
  if (hasValue) {
    // Un secondo clic sull'unico filtro attivo ripristina la vista completa.
    state.statusFilters = currentFilters.length === 1
      ? [...ALL_STATUS_VALUES]
      : currentFilters.filter((value) => value !== normalizedValue);
    return;
  }

  const nextFilters = [...currentFilters, normalizedValue];
  if (nextFilters.length === ALL_STATUS_VALUES.length) {
    state.statusFilters = [...ALL_STATUS_VALUES];
  } else {
    state.statusFilters = nextFilters;
  }
}

function isStatusFilterActive(statusValue) {
  const filters = activeStatusFilters.value;
  if (!filters.length) {
    return false;
  }

  if (filters.length === ALL_STATUS_VALUES.length) {
    return true;
  }

  const normalizedValue = normalizeStatus(statusValue);
  return filters.includes(normalizedValue);
}

function setCalendarView(mode) {
  if (!mode || calendarViewMode.value === mode) {
    return;
  }
  calendarViewMode.value = mode;
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return;
  }

  if (mode === 'week') {
    weekViewMode.value = isMobileViewport.value ? 'agenda' : 'grid';
    const start = startOfWeek(state.selectedDate || new Date());
    state.focus = start;
    state.selectedDate = start;
  }
  if (mode === 'day') {
    const day = startOfDay(state.selectedDate || new Date());
    state.focus = day;
    state.selectedDate = day;
  }
}

function goToPreviousWeek() {
  const newFocus = addDays(weekStartDate.value, -7);
  calendarState[activeCalendarKey.value].focus = newFocus;
  calendarState[activeCalendarKey.value].selectedDate = newFocus;
}

function goToNextWeek() {
  const newFocus = addDays(weekStartDate.value, 7);
  calendarState[activeCalendarKey.value].focus = newFocus;
  calendarState[activeCalendarKey.value].selectedDate = newFocus;
}

function goToCurrentWeek() {
  const today = new Date();
  const currentWeekStart = startOfWeek(today);
  calendarState[activeCalendarKey.value].focus = currentWeekStart;
  calendarState[activeCalendarKey.value].selectedDate = currentWeekStart;
}

function goToPreviousDay() {
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return;
  }
  const base = dayViewDate.value || startOfDay(new Date());
  const newFocus = addDays(base, -1);
  state.focus = newFocus;
  state.selectedDate = newFocus;
}

function goToNextDay() {
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return;
  }
  const base = dayViewDate.value || startOfDay(new Date());
  const newFocus = addDays(base, 1);
  state.focus = newFocus;
  state.selectedDate = newFocus;
}

function goToCurrentDay() {
  const state = calendarState[activeCalendarKey.value];
  if (!state) {
    return;
  }
  const today = startOfDay(new Date());
  state.focus = today;
  state.selectedDate = today;
}

function normalizeStatus(status) {
  if (!status) {
    return statusOptions[0].value;
  }

  const raw = status.toString().trim().toLowerCase();
  if (raw === 'da_confermare' || raw === 'da confermare') {
    return 'da_confermare';
  }
  if (raw === 'da_riprogrammare' || raw === 'da riprogrammare') {
    return 'da_riprogrammare';
  }
  if (raw === 'confermato') {
    return 'confermato';
  }
  if (raw === 'fatto') {
    return 'fatto';
  }

  const fallback = statusOptions.find(option => option.value === raw);
  return fallback ? fallback.value : statusOptions[0].value;
}

function normalizeOptionalStatus(status) {
  if (status === null || status === undefined) {
    return '';
  }

  const raw = status.toString().trim().toLowerCase();
  if (!raw) {
    return '';
  }
  if (raw === 'da_confermare' || raw === 'da confermare') {
    return 'da_confermare';
  }
  if (raw === 'da_riprogrammare' || raw === 'da riprogrammare') {
    return 'da_riprogrammare';
  }
  if (raw === 'confermato') {
    return 'confermato';
  }
  if (raw === 'fatto') {
    return 'fatto';
  }

  return '';
}

function getEventStatus(event) {
  if (getReminderStatusTarget(event)) {
    return normalizeOptionalStatus(event.status);
  }
  return normalizeStatus(event?.status);
}

function normalizeOperatorValue(value) {
  if (!value) {
    return '';
  }

  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '')
    .toLowerCase()
    .trim();
}

function buildOperatorLookup(options = []) {
  const map = new Map();
  const register = (candidate, option) => {
    const normalized = normalizeOperatorValue(candidate);
    if (normalized && !map.has(normalized)) {
      map.set(normalized, option);
    }
  };

  options.forEach((option) => {
    register(option.value, option);
    register(option.label, option);
    (option.aliases || []).forEach((alias) => register(alias, option));
  });

  return map;
}

function buildCollaboratorColorMap(options = []) {
  const map = new Map();
  let colorIndex = 0;

  const assignColor = (candidate, color) => {
    const normalized = normalizeOperatorValue(candidate);
    if (!normalized) {
      return;
    }
    if (!map.has(normalized)) {
      map.set(normalized, color);
    }
  };

  options.forEach((option) => {
    const canonical = normalizeOperatorValue(option?.value || option?.label);
    if (!canonical) {
      return;
    }
    let color = map.get(canonical);
    if (!color) {
      if (colorIndex < COLLABORATOR_COLOR_PALETTE.length) {
        color = COLLABORATOR_COLOR_PALETTE[colorIndex];
      } else {
        color = hslToHex((colorIndex * 37) % 360, 62, 45);
      }
      colorIndex += 1;
      map.set(canonical, color);
    }
    assignColor(option.value, color);
    assignColor(option.label, color);
    (option.aliases || []).forEach((alias) => assignColor(alias, color));
  });

  return map;
}

function resolveOperatorOption(value) {
  if (!value) {
    return null;
  }
  const normalized = normalizeOperatorValue(value);
  return OPERATOR_LOOKUP.get(normalized) || null;
}

function getOperatorLabel(value) {
  const match = resolveOperatorOption(value);
  if (match) {
    return match.label;
  }
  return (value || '').toString().trim();
}

function getOperatorValue(value) {
  const match = resolveOperatorOption(value);
  if (match) {
    return match.value;
  }
  return '';
}

function getCollaboratorKey(value) {
  if (!value) {
    return '';
  }
  const canonical = getOperatorValue(value);
  return normalizeOperatorValue(canonical || value);
}

function hashString(value) {
  let hash = 0;
  for (let idx = 0; idx < value.length; idx += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(idx);
    hash |= 0;
  }
  return hash;
}

function hslToHex(hue, saturation, lightness) {
  const h = ((hue % 360) + 360) % 360;
  const s = Math.min(Math.max(saturation, 0), 100) / 100;
  const l = Math.min(Math.max(lightness, 0), 100) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const toHex = (value) => Math.round((value + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function getDynamicCollaboratorColor(key) {
  const baseHue = Math.abs(hashString(key)) % 360;
  let hue = baseHue;
  for (let attempt = 0; attempt < 36; attempt += 1) {
    const candidate = hslToHex(hue, 62, 45);
    if (!USED_COLLABORATOR_COLORS.has(candidate)) {
      return candidate;
    }
    hue = (hue + 23) % 360;
  }
  return hslToHex(baseHue, 62, 45);
}

function getCollaboratorAccent(event) {
  if (!event) {
    return '';
  }
  if (event.isBlock) {
    return '';
  }
  const candidate = event.collaborator || (Array.isArray(event.operators) ? event.operators[0] : '');
  const key = getCollaboratorKey(candidate);
  if (!key) {
    return '';
  }
  if (event.type === COMMERCIAL_CALENDAR_ID && COMMERCIAL_COLLABORATOR_COLOR_OVERRIDES[key]) {
    return COMMERCIAL_COLLABORATOR_COLOR_OVERRIDES[key];
  }
  const mapped = COLLABORATOR_COLOR_MAP.get(key);
  if (mapped) {
    return mapped;
  }
  if (DYNAMIC_COLLABORATOR_COLORS.has(key)) {
    return DYNAMIC_COLLABORATOR_COLORS.get(key);
  }
  const dynamicColor = getDynamicCollaboratorColor(key);
  DYNAMIC_COLLABORATOR_COLORS.set(key, dynamicColor);
  USED_COLLABORATOR_COLORS.add(dynamicColor);
  return dynamicColor;
}

function resolveCollaboratorValue(source) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  return pickFirstNonEmpty(source, COLLABORATOR_FIELD_PRIORITY);
}

function resolveReminderCollaboratorValue(source, type) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const field = REMINDER_OPERATOR_FIELDS[type];
  // Each call owns its assignment: never borrow an operator from another call.
  return field ? pickFirstNonEmpty(source, [field]) : '';
}

function getContactOperatorValue(value) {
  if (!value) {
    return '';
  }
  const normalized = normalizeOperatorValue(value);
  if (!normalized) {
    return '';
  }
  return CONTACT_OPERATOR_VALUE_MAP[normalized] || getOperatorLabel(value) || String(value).trim();
}

function collectOperatorValues(values = []) {
  const map = new Map();
  values
    .map((value) => (value || '').toString().trim())
    .filter(Boolean)
    .forEach((value) => {
      const option = resolveOperatorOption(value);
      const normalized = option?.value || normalizeOperatorValue(value);
      if (!normalized) {
        return;
      }
      const display = option?.label || value;
      if (!map.has(normalized)) {
        map.set(normalized, display);
      }
    });
  return Array.from(map.values());
}

function buildHiddenOperatorSet() {
  const set = new Set();
  const register = (value) => {
    const normalized = normalizeOperatorValue(value);
    if (normalized) {
      set.add(normalized);
    }
  };

  HIDDEN_OPERATOR_OPTIONS.forEach((option) => {
    register(option.value);
    register(option.label);
    (option.aliases || []).forEach(register);
  });

  return set;
}

function getEventOperators(event) {
  if (!event) {
    return [];
  }

  if (Array.isArray(event.operators) && event.operators.length) {
    return event.operators;
  }

  return collectOperatorValues([
    event.collaborator,
    event.operatore,
    event.operatore1,
    event.operatore2,
    event.operatore3,
    event.reminderMeta?.operatore,
    event.reminderMeta?.operatore1,
    event.reminderMeta?.operatore2,
    event.reminderMeta?.operatore3
  ]);
}

function eventMatchesOperator(event, normalizedOperator) {
  if (!normalizedOperator) {
    return true;
  }

  const operators = getEventOperators(event);
  if (normalizedOperator === NO_OPERATOR_VALUE) {
    return operators.length === 0;
  }
  return operators.some((operator) => {
    const optionValue = getOperatorValue(operator);
    const normalizedCurrent = normalizeOperatorValue(operator);
    return optionValue === normalizedOperator || normalizedCurrent === normalizedOperator;
  });
}

function getEventContactId(event) {
  if (!event) {
    return '';
  }

  if (event.reminderMeta?.contactId) {
    return String(event.reminderMeta.contactId);
  }

  if (event.isReminder && event.userId) {
    return String(event.userId);
  }

  return '';
}

function getReminderOperatorField(event) {
  const type = event?.reminderMeta?.type;
  return REMINDER_OPERATOR_FIELDS[type] || REMINDER_OPERATOR_FIELDS.first;
}

function getReminderStatusTarget(event) {
  if (!event?.isReminder) {
    return null;
  }

  const contactId = String(event.reminderMeta?.contactId || '').trim();
  const reminderType = event.reminderMeta?.type;
  const hasMappedStatusField = Object.prototype.hasOwnProperty.call(
    REMINDER_CALENDAR_STATUS_FIELDS,
    reminderType
  );
  const statusField = hasMappedStatusField
    ? REMINDER_CALENDAR_STATUS_FIELDS[reminderType]
    : '';
  if (!contactId || !statusField) {
    return null;
  }

  return { contactId, reminderType, statusField };
}

function canUpdateStatus(event) {
  if (!event || event.isBlock) {
    return false;
  }
  return !event.isReminder || Boolean(getReminderStatusTarget(event));
}

function normalizeSearchValue(value) {
  if (!value) {
    return '';
  }

  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function getSearchPhoneDigits(value) {
  const text = String(value ?? '').trim();
  return /^[+\d\s()./-]+$/.test(text) ? text.replace(/\D/g, '') : '';
}

function buildEventSearchEntry(event) {
  return {
    texts: [event.name, event.user, event.email, event.phone]
      .map(normalizeSearchValue)
      .filter(Boolean),
    // Legacy reminders can carry the phone in `user`; emails are never parsed as phones.
    phones: [event.phone, event.user].map(getSearchPhoneDigits).filter(Boolean)
  };
}

function normalizeExClienteValue(value) {
  if (value === true || value === false) {
    return value;
  }
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['si', 'sì', 'yes', 'true', '1'].includes(normalized)) {
    return true;
  }
  if (['no', 'false', '0'].includes(normalized)) {
    return false;
  }
  return null;
}

function formatExClienteValue(value) {
  const normalized = normalizeExClienteValue(value);
  if (normalized === true) {
    return 'Sì';
  }
  if (normalized === false) {
    return 'No';
  }
  return '—';
}

function syncExClienteForContact(contactId, value) {
  if (!contactId) {
    return;
  }
  const normalizedId = String(contactId);
  const normalizedValue = !!value;
  const updateList = (list) => {
    list.forEach((evt) => {
      if (getEventContactId(evt) === normalizedId) {
        evt.excliente = normalizedValue;
      }
    });
  };
  updateList(calendarEvents.value);
  updateList(commercialReminderEvents.value);
  if (selectedWeekEvent.value && getEventContactId(selectedWeekEvent.value) === normalizedId) {
    selectedWeekEvent.value.excliente = normalizedValue;
  }
}

async function toggleExClienteFromEvent(event, checked) {
  const contactId = getEventContactId(event);
  if (!contactId) {
    return;
  }
  try {
    await updateDoc(doc(contactsCollectionRef, contactId), { excliente: !!checked });
    syncExClienteForContact(contactId, checked);
  } catch (error) {
    console.error('Errore durante l\'aggiornamento ex cliente:', error);
  }
}

function truncateMetaValue(value, length = META_FIELD_MAX_LENGTH) {
  if (!value) {
    return '';
  }

  const stringValue = String(value);
  if (stringValue.length <= length) {
    return stringValue;
  }

  const sliceLength = Math.max(length - 3, 0);
  return `${stringValue.slice(0, sliceLength)}...`;
}

function statusPillStyle(option, isActive) {
  const baseColor = statusColors[option.value] || '#1f2937';
  if (isActive) {
    return {
      borderColor: baseColor,
      backgroundColor: `${baseColor}1a`,
      color: baseColor
    };
  }

  return {
    borderColor: 'transparent',
    backgroundColor: 'rgba(148, 163, 184, 0.12)',
    color: '#475569'
  };
}

function getStatusPillTitle(event, option) {
  const label = option?.label || 'Stato';
  if (getReminderStatusTarget(event) && getEventStatus(event) === option?.value) {
    return `${label} — clicca di nuovo per rimuovere lo stato`;
  }
  return label;
}

function getStatusUpdateKey(event) {
  if (!event || event.isBlock) {
    return '';
  }

  if (event.isReminder) {
    const target = getReminderStatusTarget(event);
    if (!target) {
      return '';
    }
    return `reminder:${target.contactId}:${target.reminderType}`;
  }

  return event.id ? `calendar:${event.id}` : '';
}

function isStatusUpdating(event) {
  const key = getStatusUpdateKey(event);
  return Boolean(key && updatingStatusKeys.value.has(key));
}

function setStatusUpdating(key, isUpdating) {
  const nextKeys = new Set(updatingStatusKeys.value);
  if (isUpdating) {
    nextKeys.add(key);
  } else {
    nextKeys.delete(key);
  }
  updatingStatusKeys.value = nextKeys;
}

function syncReminderCalendarStatus(contactId, reminderType, status) {
  const normalizedContactId = String(contactId || '');
  const normalizedStatus = normalizeOptionalStatus(status);
  const matchesReminder = (item) => (
    item?.isReminder &&
    getEventContactId(item) === normalizedContactId &&
    item.reminderMeta?.type === reminderType
  );

  commercialReminderEvents.value.forEach((item) => {
    if (matchesReminder(item)) {
      item.status = normalizedStatus;
    }
  });

  if (matchesReminder(selectedWeekEvent.value)) {
    selectedWeekEvent.value.status = normalizedStatus;
  }
}

async function updateStatus(event, newStatus) {
  if (!canUpdateStatus(event)) {
    return;
  }

  const reminderTarget = getReminderStatusTarget(event);
  const requestedStatus = normalizeStatus(newStatus);
  const currentStatus = getEventStatus(event);
  const normalizedNew = reminderTarget && currentStatus === requestedStatus
    ? ''
    : requestedStatus;
  if (!reminderTarget && currentStatus === normalizedNew) {
    return;
  }

  const updateKey = getStatusUpdateKey(event);
  if (!updateKey || updatingStatusKeys.value.has(updateKey)) {
    return;
  }

  setStatusUpdating(updateKey, true);

  try {
    if (reminderTarget) {
      await updateDoc(doc(contactsCollectionRef, reminderTarget.contactId), {
        [reminderTarget.statusField]: normalizedNew || deleteField()
      });
      syncReminderCalendarStatus(reminderTarget.contactId, reminderTarget.reminderType, normalizedNew);
      return;
    }

    const docRef = doc(calendarCollectionRef, event.id);
    await updateDoc(docRef, { status: normalizedNew, updatedAt: Timestamp.now() });
  } catch (error) {
    console.error('Errore durante l\'aggiornamento dello status evento:', error);
  } finally {
    setStatusUpdating(updateKey, false);
  }
}

async function updateEventColor(event, colorValue) {
  if (!event || event.isReminder) {
    return;
  }

  const normalized = normalizeColorValue(colorValue);
  const current = normalizeColorValue(event.color);
  if (normalized === current) {
    return;
  }

  try {
    const docRef = doc(calendarCollectionRef, event.id);
    await updateDoc(docRef, { color: normalized, updatedAt: Timestamp.now() });
  } catch (error) {
    console.error('Errore durante l\'aggiornamento del colore evento:', error);
  }
}

function handleUserSearchInput() {
  const term = userSearchTerm.value.trim();
  const previousUser = eventForm.user;
  eventForm.user = term;
  if (!eventForm.userId || term !== previousUser) {
    eventForm.userId = '';
    eventForm.email = '';
    eventForm.phone = '';
  }

  if (userSearchTimeoutId) {
    clearTimeout(userSearchTimeoutId);
  }

  if (term.length < MIN_USER_SEARCH) {
    userSuggestions.value = [];
    userSearchDropdownOpen.value = false;
    isUserSearchLoading.value = false;
    return;
  }

  isUserSearchLoading.value = true;
  userSearchDropdownOpen.value = true;
  userSearchTimeoutId = setTimeout(() => {
    fetchUserSuggestions(term);
  }, 250);
}

async function fetchUserSuggestions(term) {
  try {
    const [surnameMatches, emailMatches] = await Promise.all([
      searchUsersBySurname(term, USER_SEARCH_FETCH_LIMIT),
      searchUsersByEmail(term, USER_SEARCH_FETCH_LIMIT)
    ]);

    if (userSearchTerm.value.trim() !== term || term.length < MIN_USER_SEARCH) {
      return;
    }

    const orderedMatches = mergeUserMatches([...surnameMatches, ...emailMatches])
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score;
        }
        const surnameSort = (a.surname || '').localeCompare(b.surname || '', 'it', { sensitivity: 'base' });
        if (surnameSort !== 0) {
          return surnameSort;
        }
        return (a.email || '').localeCompare(b.email || '', 'it', { sensitivity: 'base' });
      })
      .map((match) => ({
        id: match.id,
        name: match.name,
        surname: match.surname,
        email: match.email,
        phone: match.phone,
        display: match.display
      }));

    userSuggestions.value = orderedMatches.slice(0, MAX_USER_SUGGESTIONS);
  } catch (error) {
    console.error('Errore durante la ricerca utenti:', error);
    userSuggestions.value = [];
  } finally {
    isUserSearchLoading.value = false;
    const currentTerm = userSearchTerm.value.trim();
    userSearchDropdownOpen.value = currentTerm === term && currentTerm.length >= MIN_USER_SEARCH;
  }
}

function buildUserDisplay(name, surname, email) {
  const parts = [name, surname].filter((part) => part && part.trim().length);
  let label = parts.join(' ');
  if (!label && email) {
    label = email;
  } else if (label && email) {
    label += ` · ${email}`;
  }
  return label || 'Utente registrato';
}

function selectUserSuggestion(user) {
  eventForm.userId = user.id;
  eventForm.user = user.display;
  eventForm.email = user.email || '';
  eventForm.phone = user.phone || '';
  userSearchTerm.value = user.display;
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;
}

function clearSelectedUser() {
  eventForm.userId = '';
  eventForm.user = '';
  eventForm.email = '';
  eventForm.phone = '';
  userSearchTerm.value = '';
  userSuggestions.value = [];
  userSearchDropdownOpen.value = false;
}

function handleUserInputFocus() {
  if (userSearchTerm.value.trim().length >= MIN_USER_SEARCH) {
    if (!userSuggestions.value.length) {
      handleUserSearchInput();
    } else {
      userSearchDropdownOpen.value = true;
    }
  }
}

function handleUserInputBlur() {
  setTimeout(() => {
    userSearchDropdownOpen.value = false;
  }, 150);
}

async function searchUsersBySurname(term, fetchLimit) {
  const trimmed = term.trim();
  if (!trimmed) {
    return [];
  }

  const lower = trimmed.toLowerCase();
  const capitalized = capitalizeFirst(lower);
  const upper = trimmed.toUpperCase();
  const firstUpper = capitalized.charAt(0);
  const firstLower = firstUpper.toLowerCase();

  const surnamePrefixes = new Set();
  if (capitalized) {
    surnamePrefixes.add(capitalized);
  }
  if (trimmed && trimmed !== capitalized) {
    surnamePrefixes.add(trimmed);
  }
  if (upper && upper !== capitalized) {
    surnamePrefixes.add(upper);
  }
  if (firstUpper) {
    surnamePrefixes.add(firstUpper);
  }
  if (firstLower && firstLower !== firstUpper) {
    surnamePrefixes.add(firstLower);
  }

  if (!surnamePrefixes.size) {
    surnamePrefixes.add('');
  }

  const queries = [];

  Array.from(surnamePrefixes).forEach((value) => {
    queries.push(
      getDocs(
        query(
          usersCollectionRef,
          orderBy('surname'),
          startAt(value),
          endAt(`${value}\uf8ff`),
          limit(fetchLimit)
        )
      )
    );
  });

  if (lower) {
    queries.push(
      getDocs(
        query(
          usersCollectionRef,
          where('surnameLower', '>=', lower),
          where('surnameLower', '<=', `${lower}\uf8ff`),
          limit(fetchLimit)
        )
      )
    );
    queries.push(
      getDocs(
        query(
          usersCollectionRef,
          orderBy('surnameLower'),
          startAt(lower),
          endAt(`${lower}\uf8ff`),
          limit(fetchLimit)
        )
      )
    );
  }

  queries.push(
    getDocs(
      query(
        usersCollectionRef,
        orderBy('surname'),
        limit(fetchLimit)
      )
    )
  );

  const snapshots = await Promise.allSettled(queries);
  const matchesMap = new Map();

  snapshots.forEach((result) => {
    if (result.status !== 'fulfilled') {
      console.warn('Ricerca cognome non riuscita per una delle query:', result.reason);
      return;
    }

    result.value.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const name = (data.name || '').toString();
      const surname = (data.surname || '').toString();
      const email = (data.email || '').toString();
      const phone = (data.phone || '').toString();
      const surnameCandidate =
        (data.surnameLower && data.surnameLower.toString().trim()) ||
        surname.trim().toLowerCase();
      const surnameNormalized = surnameCandidate.toLowerCase();

      if (!surnameNormalized || !surnameNormalized.includes(lower)) {
        return;
      }

      const score = surnameNormalized.startsWith(lower) ? 0 : surnameNormalized.indexOf(lower);
      const existing = matchesMap.get(docSnap.id);
      if (!existing || score < existing.score) {
        matchesMap.set(docSnap.id, {
          id: docSnap.id,
          name,
          surname,
          email,
          phone,
          display: buildUserDisplay(name, surname, email),
          score
        });
      }
    });
  });

  return Array.from(matchesMap.values());
}

async function searchUsersByEmail(term, fetchLimit) {
  const trimmed = term.trim();
  if (!trimmed) {
    return [];
  }

  const lower = trimmed.toLowerCase();
  const queries = [
    getDocs(query(usersCollectionRef, where('emailLower', '==', lower), limit(fetchLimit))),
    getDocs(query(usersCollectionRef, where('email', '==', lower), limit(fetchLimit))),
    getDocs(
      query(
        usersCollectionRef,
        where('emailLower', '>=', lower),
        where('emailLower', '<=', `${lower}\uf8ff`),
        limit(fetchLimit)
      )
    ),
    getDocs(
      query(
        usersCollectionRef,
        orderBy('email'),
        startAt(lower),
        endAt(`${lower}\uf8ff`),
        limit(fetchLimit)
      )
    ),
    getDocs(
      query(
        usersCollectionRef,
        orderBy('email'),
        limit(fetchLimit)
      )
    )
  ];

  const snapshots = await Promise.allSettled(queries);
  const matchesMap = new Map();

  snapshots.forEach((result) => {
    if (result.status !== 'fulfilled') {
      console.warn('Ricerca email non riuscita per una delle query:', result.reason);
      return;
    }

    result.value.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const name = (data.name || '').toString();
      const surname = (data.surname || '').toString();
      const email = (data.email || '').toString();
      const phone = (data.phone || '').toString();
      const emailCandidate =
        (data.emailLower && data.emailLower.toString().trim()) ||
        email.trim().toLowerCase();
      const emailNormalized = emailCandidate.toLowerCase();

      if (!emailNormalized || !emailNormalized.includes(lower)) {
        return;
      }

      const matchIndex = emailNormalized.indexOf(lower);
      const score = emailNormalized === lower
        ? -1
        : (emailNormalized.startsWith(lower) ? 0 : matchIndex + 2);
      const existing = matchesMap.get(docSnap.id);
      if (!existing || score < existing.score) {
        matchesMap.set(docSnap.id, {
          id: docSnap.id,
          name,
          surname,
          email,
          phone,
          display: buildUserDisplay(name, surname, email),
          score
        });
      }
    });
  });

  return Array.from(matchesMap.values());
}

function mergeUserMatches(matches = []) {
  const matchesMap = new Map();
  matches.forEach((match) => {
    if (!match?.id) {
      return;
    }
    const existing = matchesMap.get(match.id);
    if (!existing) {
      matchesMap.set(match.id, match);
      return;
    }
    matchesMap.set(match.id, {
      ...existing,
      ...match,
      score: Math.min(
        existing.score ?? Number.MAX_SAFE_INTEGER,
        match.score ?? Number.MAX_SAFE_INTEGER
      )
    });
  });
  return Array.from(matchesMap.values());
}

function buildCommercialReminderEvents(contact) {
  const displayName = buildContactDisplayName(contact);
  const contactId = contact.uid || contact.id || contact.docId || '';
  const phone = (contact.telefono || contact.phone || '').toString();
  const email = (contact.email || '').toString();
  const baseNotes = contact.note || '';
  const excliente = normalizeExClienteValue(contact.excliente);

  const occurrences = [
    {
      dateField: 'dataricontatto2',
      timeField: 'orario',
      label: 'Promemoria commerciale',
      type: 'first'
    },
    {
      dateField: 'dataricontatto3',
      timeField: 'orario2',
      label: 'Secondo promemoria commerciale',
      type: 'second'
    }
  ];

  return occurrences
    .map(({ dateField, timeField, label, type }) => {
      const dateValue = toSafeDate(contact[dateField]);
      if (!dateValue) {
        return null;
      }

      const startDateTime = combineDateAndTime(dateValue, contact[timeField]);
      const endDateTime = addMinutes(startDateTime, COMMERCIAL_REMINDER_DURATION);
      const collaboratorRaw = resolveReminderCollaboratorValue(contact, type);
      const collaborator = getOperatorLabel(collaboratorRaw);
      const operators = collectOperatorValues([collaboratorRaw]);
      const statusField = type === 'second' ? 'stato3' : 'stato2';
      const reminderStatus = contact[statusField] || contact.stato || '';
      const calendarStatusField = REMINDER_CALENDAR_STATUS_FIELDS[type];

      return {
        id: `${contactId || 'contatto'}-reminder-${type}`,
        type: COMMERCIAL_CALENDAR_ID,
        name: displayName,
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        duration: COMMERCIAL_REMINDER_DURATION,
        collaborator,
        operators,
        user: email || phone || displayName,
        userId: contactId || '',
        notes: baseNotes,
        excliente,
        status: normalizeOptionalStatus(contact[calendarStatusField]),
        isBlock: false,
        isReminder: true,
        reminderLabel: label,
        phone,
        email,
        reminderMeta: {
          contactId: contactId || '',
          type,
          time: contact[timeField] || '',
          stato: reminderStatus
        }
      };
    })
    .filter(Boolean);
}

function buildContactDisplayName(contact) {
  const parts = [contact.name, contact.surname]
    .map((value) => (value || '').toString().trim())
    .filter(Boolean);

  if (parts.length) {
    return parts.join(' ');
  }

  if (contact.email) {
    return contact.email;
  }

  if (contact.telefono || contact.phone) {
    return (contact.telefono || contact.phone).toString();
  }

  return 'Contatto senza nome';
}

function capitalizeFirst(value) {
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function pickFirstNonEmpty(source, fields) {
  for (const field of fields) {
    const value = source[field];
    if (value && value.toString().trim()) {
      return value;
    }
  }
  return '';
}

function toSafeDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    const clone = new Date(value);
    return Number.isNaN(clone.getTime()) ? null : clone;
  }

  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value === 'number') {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value === 'string') {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (value.seconds) {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  return null;
}

function combineDateAndTime(dateValue, timeString) {
  const base = new Date(dateValue);
  if (!timeString || typeof timeString !== 'string') {
    base.setHours(9, 0, 0, 0);
    return base;
  }

  const match = timeString.match(/(\d{1,2})(?::|\.)(\d{1,2})/);
  if (!match) {
    base.setHours(9, 0, 0, 0);
    return base;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    base.setHours(9, 0, 0, 0);
    return base;
  }

  base.setHours(hours, minutes, 0, 0);
  return base;
}

function addMinutes(dateValue, minutes) {
  const result = new Date(dateValue);
  const parsedMinutes = Number(minutes) || 0;
  result.setMinutes(result.getMinutes() + parsedMinutes);
  return result;
}

function startOfDay(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function isSameDay(dateA, dateB) {
  if (!dateA || !dateB) {
    return false;
  }
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

function formatForInput(date) {
  const target = new Date(date);
  target.setMinutes(target.getMinutes() - target.getTimezoneOffset());
  return target.toISOString().slice(0, 16);
}

function formatTimeRange(startISO, endISO) {
  if (!startISO) {
    return '';
  }

  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const timeFormatter = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const startLabel = timeFormatter.format(start);

  if (!endISO) {
    return startLabel;
  }

  const end = new Date(endISO);
  if (Number.isNaN(end.getTime()) || end.getTime() === start.getTime()) {
    return startLabel;
  }

  return `${startLabel} - ${timeFormatter.format(end)}`;
}

function formatEventDateLabel(dateISO) {
  if (!dateISO) {
    return '';
  }

  const date = new Date(dateISO);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('it-IT', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
}

function formatToolbarDate(date) {
  if (!date) {
    return '';
  }

  const formatted = new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date);

  return formatted
    .split(' ')
    .map((word) => capitalizeFirst(word))
    .join(' ')
    .replace(/\bDi\b/gi, 'di'); // keep prepositions lower-case
}

function formatZoomLabel(value) {
  const percentage = Math.round(Number(value) * 100);
  if (!Number.isFinite(percentage)) {
    return '';
  }
  return `${percentage}%`;
}

function normalizeWeekViewZoom(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const matched = WEEK_VIEW_ZOOM_OPTIONS.find((option) => Math.abs(option - numeric) < 0.001);
  return typeof matched === 'number' ? matched : null;
}

function getStoredWeekViewZoom() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(WEEK_VIEW_ZOOM_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return normalizeWeekViewZoom(stored);
  } catch (error) {
    console.warn('Impossibile recuperare il livello di zoom settimana:', error);
    return null;
  }
}

function persistWeekViewZoom(value) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(WEEK_VIEW_ZOOM_STORAGE_KEY, value.toString());
  } catch (error) {
    console.warn('Impossibile salvare il livello di zoom settimana:', error);
  }
}

function getStoredTimeBlockVisibility() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(TIME_BLOCK_VISIBILITY_STORAGE_KEY);
    if (stored === 'true') {
      return true;
    }
    if (stored === 'false') {
      return false;
    }
    return null;
  } catch (error) {
    console.warn('Impossibile recuperare la visibilità dei blocchi orari:', error);
    return null;
  }
}

function persistTimeBlockVisibility(value) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(TIME_BLOCK_VISIBILITY_STORAGE_KEY, String(Boolean(value)));
  } catch (error) {
    console.warn('Impossibile salvare la visibilità dei blocchi orari:', error);
  }
}

function buildWeekViewEvent(event, minuteHeight = WEEK_VIEW_BASE_MINUTE_HEIGHT) {
  const start = new Date(event.start);
  const end = new Date(event.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const durationMinutes = Math.max(Math.round((end - start) / 60000), 15);
  const endMinutes = Math.min(startMinutes + durationMinutes, WEEK_VIEW_END_MINUTE);
  if (endMinutes <= WEEK_VIEW_START_MINUTE) {
    return null;
  }

  const visibleStart = Math.max(startMinutes, WEEK_VIEW_START_MINUTE);
  const visibleEnd = Math.max(visibleStart, Math.min(endMinutes, WEEK_VIEW_END_MINUTE));
  const pixelRatio = Number.isFinite(minuteHeight) && minuteHeight > 0 ? minuteHeight : WEEK_VIEW_BASE_MINUTE_HEIGHT;
  const visibleMinutes = visibleEnd - visibleStart;
  const isBlock = Boolean(event.isBlock);
  const eventHeightPx = isBlock
    ? Math.max(visibleMinutes * pixelRatio, 1)
    : Math.max(visibleMinutes * pixelRatio, WEEK_VIEW_MIN_EVENT_HEIGHT);
  const layoutEndMinutes = isBlock
    ? visibleEnd
    : Math.min(visibleStart + eventHeightPx / pixelRatio, WEEK_VIEW_END_MINUTE);
  const accent = getEventAccent(event, getCalendarAccent(event.type));
  const normalizedStatus = getEventStatus(event);
  const hasCustomColor = Boolean(normalizeColorValue(event.color));
  const hasCollaboratorColor = Boolean(getCollaboratorAccent(event));
  const textColor = isBlock
    ? getReadableTextColor(accent, '#ffffff')
    : ((hasCustomColor || hasCollaboratorColor)
      ? getReadableTextColor(accent, '#0f172a')
      : '#0f172a');

  return {
    id: event.id,
    source: event,
    name: event.name,
    isBlock,
    isReminder: Boolean(event.isReminder),
    startMinutes,
    endMinutes,
    layoutStartMinutes: visibleStart,
    layoutEndMinutes,
    topPx: (visibleStart - WEEK_VIEW_START_MINUTE) * pixelRatio,
    timeEndPx: (visibleEnd - WEEK_VIEW_START_MINUTE) * pixelRatio,
    durationMinutes,
    timeLabel: formatTimeRange(event.start, event.end),
    status: normalizedStatus,
    accent,
    textColor,
    statusIcon: statusIcons[normalizedStatus],
    style: {
      top: `${(visibleStart - WEEK_VIEW_START_MINUTE) * pixelRatio}px`,
      height: `${eventHeightPx}px`,
      borderLeft: `3px solid ${accent}`,
      backgroundColor: isBlock ? colorWithAlpha(accent, 0.1) : accent,
      color: isBlock ? '#0f172a' : textColor,
      left: '0%',
      width: '100%',
      zIndex: isBlock ? WEEK_VIEW_BLOCK_Z_BASE : WEEK_VIEW_EVENT_Z_BASE
    }
  };
}

function formatWeekEventTooltip(event) {
  if (!event) {
    return '';
  }
  return [
    event.name || (event.isBlock ? 'Blocco orario' : 'Evento senza titolo'),
    event.timeLabel,
    event.source?.collaborator
  ].filter(Boolean).join(' · ');
}

function buildTimeBlockInteractionGroups(events = []) {
  return buildOverlapGroups(events.filter((event) => event?.isBlock)).map((group) => {
    const sortedEvents = group.slice().sort(compareWeekLayoutEvents);
    const firstEvent = sortedEvents[0];
    const lastEvent = sortedEvents.reduce((latest, event) => (
      event.timeEndPx > latest.timeEndPx ? event : latest
    ), firstEvent);
    const topPx = Math.min(...sortedEvents.map((event) => event.topPx));
    const bottomPx = Math.max(...sortedEvents.map((event) => event.timeEndPx));
    const accents = [...new Set(sortedEvents.map((event) => event.accent).filter(Boolean))];
    const timeLabel = formatTimeRange(firstEvent.source?.start, lastEvent.source?.end);
    const title = sortedEvents.length === 1
      ? formatWeekEventTooltip(firstEvent)
      : `${sortedEvents.length} blocchi orari · ${timeLabel}`;
    const ariaLabel = sortedEvents.length === 1
      ? `Apri blocco orario: ${title}`
      : `Apri elenco di ${sortedEvents.length} blocchi orari sovrapposti, ${timeLabel}`;

    return {
      id: sortedEvents.map((event) => event.id).join('-'),
      events: sortedEvents,
      timeLabel,
      title,
      ariaLabel,
      style: {
        top: `${topPx}px`,
        height: `${Math.max(bottomPx - topPx, 24)}px`,
        '--time-block-accent': accents[0] || '#334155',
        '--time-block-accent-secondary': accents[1] || accents[0] || '#334155'
      }
    };
  });
}

function layoutWeekDayEvents(events, options = {}) {
  if (!Array.isArray(events) || !events.length) {
    return [];
  }

  const aggregateOverflow = options.aggregateOverflow !== false;
  const maxVisibleColumns = Number.isFinite(options.maxVisibleColumns)
    ? Math.max(1, Math.floor(options.maxVisibleColumns))
    : WEEK_VIEW_MAX_VISIBLE_APPOINTMENT_COLUMNS;
  const blockEvents = events.filter((event) => event.isBlock);
  const appointmentEvents = events.filter((event) => !event.isBlock);
  const laidOutEvents = [];

  buildOverlapGroups(blockEvents).forEach((group) => {
    const totalColumns = assignWeekEventColumns(group);
    group.forEach((event) => {
      applyTimeBlockBandStyle(event, totalColumns);
      laidOutEvents.push(event);
    });
  });

  buildOverlapGroups(appointmentEvents).forEach((group) => {
    const totalColumns = assignWeekEventColumns(group);
    if (!aggregateOverflow || totalColumns <= maxVisibleColumns) {
      group.forEach((event) => {
        applyWeekEventStyle(event, totalColumns, options);
        laidOutEvents.push(event);
      });
      return;
    }

    const visibleEvents = group.filter((event) => event.column < maxVisibleColumns);
    const hiddenEvents = group.filter((event) => event.column >= maxVisibleColumns);
    const hiddenByHost = new Map();

    hiddenEvents.forEach((hiddenEvent) => {
      const directlyOverlapping = visibleEvents.filter((visibleEvent) => eventsOverlap(visibleEvent, hiddenEvent));
      const hiddenStart = getWeekEventLayoutStart(hiddenEvent);
      const activeAtHiddenStart = directlyOverlapping.filter((event) => (
        getWeekEventLayoutStart(event) <= hiddenStart && getWeekEventLayoutEnd(event) > hiddenStart
      ));
      const eligibleHosts = activeAtHiddenStart.length ? activeAtHiddenStart : directlyOverlapping;
      const preferredLane = maxVisibleColumns - 1;
      const preferredHosts = eligibleHosts.filter((event) => event.column === preferredLane);
      const candidates = preferredHosts.length ? preferredHosts : eligibleHosts;
      const host = candidates.slice().sort((eventA, eventB) => {
        const overlapDifference = getWeekEventOverlapDuration(eventB, hiddenEvent)
          - getWeekEventOverlapDuration(eventA, hiddenEvent);
        return overlapDifference || compareWeekLayoutEvents(eventA, eventB);
      })[0] || visibleEvents[0];

      if (!host) {
        return;
      }
      const assigned = hiddenByHost.get(host) || [];
      assigned.push(hiddenEvent);
      hiddenByHost.set(host, assigned);
    });

    visibleEvents.forEach((event) => {
      const assignedHiddenEvents = (hiddenByHost.get(event) || []).sort(compareWeekLayoutEvents);
      if (assignedHiddenEvents.length) {
        event.overlapEvents = [event, ...assignedHiddenEvents]
          .map((groupedEvent) => groupedEvent.source)
          .filter(Boolean);
        event.overlapCount = assignedHiddenEvents.length;
      }
      applyWeekEventStyle(event, maxVisibleColumns, options);
      laidOutEvents.push(event);
    });
  });

  return laidOutEvents.sort((eventA, eventB) => {
    if (eventA.isBlock !== eventB.isBlock) {
      return eventA.isBlock ? -1 : 1;
    }
    return compareWeekLayoutEvents(eventA, eventB);
  });
}

function compareWeekLayoutEvents(eventA, eventB) {
  const startDifference = getWeekEventLayoutStart(eventA) - getWeekEventLayoutStart(eventB);
  if (startDifference) {
    return startDifference;
  }
  const durationA = getWeekEventLayoutEnd(eventA) - getWeekEventLayoutStart(eventA);
  const durationB = getWeekEventLayoutEnd(eventB) - getWeekEventLayoutStart(eventB);
  if (durationA !== durationB) {
    return durationB - durationA;
  }
  return String(eventA?.id || '').localeCompare(String(eventB?.id || ''));
}

function getWeekEventLayoutStart(event) {
  return Number.isFinite(event?.layoutStartMinutes)
    ? event.layoutStartMinutes
    : (event?.startMinutes || 0);
}

function getWeekEventLayoutEnd(event) {
  return Number.isFinite(event?.layoutEndMinutes)
    ? event.layoutEndMinutes
    : (event?.endMinutes || getWeekEventLayoutStart(event));
}

function assignWeekEventColumns(events) {
  const columnAvailability = [];
  events.slice().sort(compareWeekLayoutEvents).forEach((event) => {
    const eventStart = getWeekEventLayoutStart(event);
    const eventEnd = getWeekEventLayoutEnd(event);
    let columnIndex = columnAvailability.findIndex((availableAt) => availableAt <= eventStart);

    if (columnIndex === -1) {
      columnIndex = columnAvailability.length;
      columnAvailability.push(eventEnd);
    } else {
      columnAvailability[columnIndex] = eventEnd;
    }

    event.column = columnIndex;
  });

  return Math.max(columnAvailability.length, 1);
}

function buildOverlapGroups(events) {
  const groups = [];
  const visited = new Set();

  events.slice().sort(compareWeekLayoutEvents).forEach((event) => {
    if (visited.has(event)) {
      return;
    }
    const stack = [event];
    const group = [];
    visited.add(event);

    while (stack.length) {
      const current = stack.pop();
      group.push(current);

      events.forEach((candidate) => {
        if (visited.has(candidate) || candidate === current) {
          return;
        }
        if (eventsOverlap(current, candidate)) {
          visited.add(candidate);
          stack.push(candidate);
        }
      });
    }

    groups.push(group.sort(compareWeekLayoutEvents));
  });

  return groups;
}

function eventsOverlap(eventA, eventB) {
  if (!eventA || !eventB) {
    return false;
  }
  return (
    getWeekEventLayoutStart(eventA) < getWeekEventLayoutEnd(eventB) &&
    getWeekEventLayoutStart(eventB) < getWeekEventLayoutEnd(eventA)
  );
}

function getWeekEventOverlapDuration(eventA, eventB) {
  return Math.max(
    0,
    Math.min(getWeekEventLayoutEnd(eventA), getWeekEventLayoutEnd(eventB))
      - Math.max(getWeekEventLayoutStart(eventA), getWeekEventLayoutStart(eventB))
  );
}

function applyWeekEventStyle(event, totalColumns, options = {}) {
  if (!event?.style) {
    return;
  }

  const columnGap = Number.isFinite(options.columnGap)
    ? Math.max(options.columnGap, 0)
    : WEEK_VIEW_COLUMN_GAP;
  const inset = Number.isFinite(options.columnInset)
    ? Math.max(options.columnInset, 0)
    : WEEK_VIEW_COLUMN_INSET;
  const columns = Math.max(Math.floor(totalColumns || 1), 1);
  const columnIndex = Math.min(Math.max(event.column || 0, 0), columns - 1);
  const widthDeduction = (2 * inset + (columns - 1) * columnGap) / columns;
  const leftOffset = inset + columnIndex * (columnGap - widthDeduction);

  event.style.left = `calc(${(columnIndex * 100) / columns}% + ${leftOffset}px)`;
  event.style.width = `calc(${100 / columns}% - ${widthDeduction}px)`;
  event.style.minWidth = '0';
  event.style.maxWidth = `calc(100% - ${inset}px)`;
  event.style.boxSizing = 'border-box';
  const baseZIndex = event.isBlock ? WEEK_VIEW_BLOCK_Z_BASE : WEEK_VIEW_EVENT_Z_BASE;
  event.style.zIndex = baseZIndex + columnIndex;
}

function applyTimeBlockBandStyle(event, totalColumns) {
  if (!event?.style) {
    return;
  }

  const laneCount = Math.max(Math.floor(totalColumns || 1), 1);
  const lane = Math.min(Math.max(event.column || 0, 0), laneCount - 1);
  const visibleLane = Math.min(lane, 5);
  const leftInset = 2 + visibleLane * 4;
  event.timeBlockLane = lane;
  event.style.left = `${leftInset}px`;
  event.style.width = `calc(100% - ${leftInset + 2}px)`;
  event.style.minWidth = '0';
  event.style.maxWidth = 'none';
  event.style.boxSizing = 'border-box';
  event.style.zIndex = WEEK_VIEW_BLOCK_Z_BASE + visibleLane;
}

function normalizeColorValue(value) {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : '';
}

function colorWithAlpha(color, alpha) {
  const hex = normalizeColorValue(color);
  if (!hex) {
    return color;
  }

  let value = hex.slice(1);
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const numeric = parseInt(value, 16);
  const boundedAlpha = Math.min(Math.max(Number(alpha) || 0, 0), 1);
  return `rgba(${(numeric >> 16) & 255}, ${(numeric >> 8) & 255}, ${numeric & 255}, ${boundedAlpha})`;
}

function getReadableTextColor(color, fallback = '#0f172a') {
  const hex = normalizeColorValue(color);
  if (!hex) {
    return fallback;
  }

  let value = hex.slice(1);
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const numeric = parseInt(value, 16);
  if (Number.isNaN(numeric)) {
    return fallback;
  }

  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.65 ? '#0f172a' : '#ffffff';
}

function getEventAccent(event, fallbackAccent) {
  const customColor = normalizeColorValue(event?.color);
  if (event?.isBlock) {
    return customColor || '#111827';
  }

  const collaboratorAccent = getCollaboratorAccent(event);
  if (collaboratorAccent) {
    return collaboratorAccent;
  }

  if (customColor) {
    return customColor;
  }

  if (event?.isReminder) {
    return fallbackAccent || getCalendarAccent(event?.type);
  }

  const normalized = normalizeStatus(event?.status);
  return statusColors[normalized] || fallbackAccent || '#2563eb';
}

function formatWeekDayLabel(date) {
  const formatter = new Intl.DateTimeFormat('it-IT', { weekday: 'short' });
  return formatter
    .format(date)
    .replace(/\./g, '')
    .toUpperCase();
}

function formatWeekRangeLabel(start, end) {
  if (!start || !end) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  const startLabel = capitalizeFirst(formatter.format(start));
  const endLabel = capitalizeFirst(formatter.format(end));

  return `Settimana ${startLabel} – ${endLabel}`;
}

function formatHourLabel(hour) {
  return `${hour.toString().padStart(2, '0')}:00`;
}

function startOfWeek(date) {
  const target = startOfDay(date);
  const day = target.getDay(); // 0 (Sun) - 6 (Sat)
  const diff = day === 0 ? -6 : 1 - day; // Monday as start
  target.setDate(target.getDate() + diff);
  return target;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

</script>

<style scoped>

.dashboard-layout,
.content-container {
  min-width: 0;
}

.vc-pane {
  min-width: 457px!important;
}


.calendars-dashboard {
  padding: 2.5rem 3rem;
  background: #f7f9fc;
  min-height: 100vh;
  color: #0f172a;
  width: 100%;
  box-sizing: border-box;
}

.dashboard-header {
  margin-bottom: 2.5rem;
}

.dashboard-header h1 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.dashboard-header p {
  color: #475569;
  font-size: 0.95rem;
}

.realtime-badge {
  position: fixed;
  top: 88px;
  right: 24px;
  z-index: 1400;
  width: min(360px, calc(100vw - 48px));
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.97);
  color: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-left: 5px solid var(--notification-accent, #2563eb);
  border-radius: 14px;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(10px);
}

.realtime-badge__content {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 7px;
  padding: 14px 48px 15px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
}

.realtime-badge__content--action {
  cursor: pointer;
  transition: background 0.15s ease;
}

.realtime-badge__content--action:hover {
  background: color-mix(in srgb, var(--notification-accent, #2563eb) 7%, transparent);
}

.realtime-badge__content--action:focus-visible {
  outline: 2px solid var(--notification-accent, #2563eb);
  outline-offset: -3px;
}

.realtime-badge__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.realtime-badge__icon {
  color: var(--notification-accent, #2563eb);
  font-size: 20px;
}

.realtime-badge__eyebrow {
  min-width: 0;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.055em;
  line-height: 1.25;
  text-transform: uppercase;
}

.realtime-badge__close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.realtime-badge__close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.realtime-badge__close:focus-visible {
  outline: 2px solid var(--notification-accent, #2563eb);
  outline-offset: 2px;
}

.realtime-badge__close .material-icons {
  font-size: 18px;
}

.realtime-badge__name {
  overflow-wrap: anywhere;
  font-size: 1rem;
  line-height: 1.35;
}

.realtime-badge__meta {
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
}

.realtime-badge__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 2px;
  color: var(--notification-accent, #2563eb);
  font-size: 0.78rem;
  font-weight: 800;
}

.realtime-badge__cta .material-icons {
  font-size: 16px;
  transition: transform 0.15s ease;
}

.realtime-badge__content--action:hover .realtime-badge__cta .material-icons,
.realtime-badge__content--action:focus-visible .realtime-badge__cta .material-icons {
  transform: translateX(2px);
}

.realtime-badge-enter-active,
.realtime-badge-leave-active {
  transition: opacity 0.2s ease, transform 0.22s ease;
}

.realtime-badge-enter-from,
.realtime-badge-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

.calendar-grid {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

:deep(.vc-day) {
  width: 50px;
  height: 50px;
}

:deep(.vc-day-content) {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 50px;
  padding-top: 11px;
}

:deep(.vc-dots) {
  display: grid;
  grid-template-columns: repeat(5, min-content);
  gap: 3px;
  justify-content: center;
  align-items: center;
}

:deep(.vc-dot) {
  margin: 0 !important;
}

:deep(.vc-dot--more) {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: transparent !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1;
  position: relative;
  color: inherit;
}

:deep(.vc-dot--more)::after {
  content: '+';
  position: absolute;
  transform: translateY(-0.5px);
}

:deep(.vc-day-layer-dots) {
  top: calc(11px + 28px + 7px);
  bottom: auto;
  height: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  pointer-events: none;
}

:deep(.vc-day-layer-dots .vc-day-layer-content) {
  width: 100%;
}

.calendar-switcher {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.calendar-toggle {
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 999px;
  padding: 0.6rem 1.6rem;
  background: #ffffff;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.calendar-toggle:hover {
  border-color: rgba(15, 23, 42, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
}

.calendar-toggle--active {
  background: var(--accent, #2563eb);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 18px 28px rgba(37, 99, 235, 0.22);
}

.calendar-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.08);
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 0 0 2.5rem;
}

.calendar-card--full {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: auto 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
}

.calendar-card--week {
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  grid-auto-flow: row;
  gap: 1.5rem;
}

.card-header {
  position: relative;
  display: block;
  padding: 1.1rem 1.65rem;
  margin: calc(-1 * min(1.75rem, 4vw)) calc(-1 * min(1.75rem, 4vw)) 0;
  border-radius: 18px;
  background: linear-gradient(135deg, #bfbfbf 0%, #afafaf 100%) !important;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.14);
  color: #ffffff;
}

.calendar-controls {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;
  gap: 0.8rem;
}

.title-group {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.85rem;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap;
  padding-left: 0.25rem;
}

.title-group h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: inherit;
  letter-spacing: 0.01em;
}

.calendar-badge {
  width: 14px;
  height: 40px;
  border-radius: 999px;
  display: inline-block;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.2);
}

.primary-button,
.secondary-button,
.icon-button {
  cursor: pointer;
  border: none;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  font-weight: 600;
}

.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.35rem;
  background: #111827;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(17, 24, 39, 0.15);
  text-align: center;
  line-height: 1.2;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.2);
}

.secondary-button {
  padding: 0.55rem 1.4rem;
  background: transparent;
  color: #111827;
  border: 1px solid rgba(15, 23, 42, 0.15);
}

.secondary-button:hover {
  border-color: rgba(15, 23, 42, 0.35);
}

@media (min-width: 1181px) {
  .title-group {
    padding-left: 0.4rem;
  }

  .calendar-controls {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }

  .calendar-controls__buttons {
    width: auto;
    grid-template-columns: repeat(2, minmax(150px, auto));
  }
}

@media (max-width: 1180px) {
  .card-header {
    text-align: left;
  }

  .title-group {
    justify-content: flex-start;
    padding-left: 0;
  }

  .calendar-controls {
    grid-template-columns: minmax(0, 1fr);
  }

  .calendar-controls__buttons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.icon-button {
  background: transparent;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.calendar-widget {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  width: 100%;
  min-height: 640px;
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-list--week {
  grid-column: 1 / -1;
  gap: 1.25rem;
}

.event-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.event-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.event-toolbar h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  text-transform: none;
}

.event-toolbar__info {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.event-toolbar__hint {
  font-size: 0.8rem;
  color: #94a3b8;
  white-space: nowrap;
}

.event-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.calendar-card--week .event-toolbar {
  padding-top: 0.25rem;
}

.event-search {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
  flex: 1 1 220px;
  max-width: 320px;
}

.event-search__icon {
  font-size: 1.1rem;
  color: #94a3b8;
}

.event-search__input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  color: #0f172a;
  flex: 1 1 auto;
  min-width: 0;
}

.event-search__input::placeholder {
  color: #94a3b8;
}

.event-search__clear {
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 28px;
  height: 28px;
  color: #94a3b8;
  cursor: pointer;
}

.event-search__clear:hover {
  color: #0f172a;
}

.operator-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
}

.operator-filter__icon {
  font-size: 1.1rem;
  color: #94a3b8;
}

.operator-filter__select {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  color: #0f172a;
}

.operator-filter__clear {
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 28px;
  height: 28px;
  color: #94a3b8;
  cursor: pointer;
}

.operator-filter__clear:hover {
  color: #0f172a;
}

.status-filter-group {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.status-filter-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.status-filter-chip:hover {
  border-color: rgba(148, 163, 184, 0.7);
  color: #1f2937;
  transform: translateY(-1px);
}

.status-filter-chip__icon {
  font-size: 1.1rem;
}

.status-filter-chip--active {
  background: var(--status-color, #2563eb);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
}

.status-filter-chip--active:hover {
  color: #ffffff;
  opacity: 0.92;
  transform: translateY(-1px);
}

.event-counter {
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid;
  color: inherit;
}

.week-toggle-button {
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(248, 250, 252, 0.9);
  color: #0f172a;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.week-toggle-button:hover {
  transform: translateY(-1px);
  border-color: rgba(15, 23, 42, 0.35);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}


.week-toggle-button--active {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  border-color: rgba(15, 23, 42, 0.35);
}

.view-toggle {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(248, 250, 252, 0.9);
  overflow: hidden;
  width: 100%;
  min-width: 0;
}

.view-toggle__btn {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.view-toggle__btn:hover {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.view-toggle__btn--active {
  background: #0f172a;
  color: #ffffff;
}

.calendar-controls__buttons {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.calendar-controls .primary-button,
.calendar-controls .secondary-button {
  width: 100%;
  min-width: 0;
}

@media (max-width: 1180px) {
  .card-header {
    gap: 1rem;
  }
}

.week-view-card {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 18px;
  padding: 1.5rem;
  background: #ffffff;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.week-toolbar-secondary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
  width: 100%;
}

.week-toolbar-secondary__left,
.week-toolbar-secondary__right {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.week-view-mode-toggle {
  display: inline-flex;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 999px;
  overflow: hidden;
  background: #ffffff;
}

.week-view-mode-toggle__btn {
  padding: 0.35rem 0.9rem;
  border: none;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.week-view-mode-toggle__btn:hover {
  background: rgba(148, 163, 184, 0.12);
  color: #0f172a;
}

.week-view-mode-toggle__btn--active {
  background: #111827;
  color: #ffffff;
}

.time-block-toggle {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 0.25rem 0.42rem 0.25rem 0.62rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 999px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.time-block-toggle:hover {
  border-color: rgba(51, 65, 85, 0.45);
  color: #0f172a;
}

.time-block-toggle:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.6);
  outline-offset: 2px;
}

.time-block-toggle--active {
  border-color: rgba(51, 65, 85, 0.45);
  background: #f8fafc;
  color: #0f172a;
}

.time-block-toggle__icon {
  font-size: 16px;
}

.time-block-toggle__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.28rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.66rem;
  font-weight: 800;
}

.time-block-toggle__switch {
  position: relative;
  width: 28px;
  height: 17px;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.15s ease;
}

.time-block-toggle__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.28);
  transition: transform 0.15s ease;
}

.time-block-toggle--active .time-block-toggle__switch {
  background: #0f172a;
}

.time-block-toggle--active .time-block-toggle__knob {
  transform: translateX(11px);
}

.week-zoom-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  border-radius: 999px;
  background: #ffffff;
  padding: 0.15rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  margin-left: 0.75rem;
}

.week-zoom-toggle__btn {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.week-zoom-toggle__btn--active {
  background: #0f172a;
  color: #ffffff;
}

.ghost-button {
  padding: 0.55rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #ffffff;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.ghost-button:hover {
  transform: translateY(-1px);
  border-color: rgba(15, 23, 42, 0.5);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

.week-nav-button {
  padding: 0.45rem 0.8rem;
  border-radius: 10px;
}

.week-nav-button .material-icons {
  font-size: 1.1rem;
}

.week-toolbar-chip {
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
}

.week-view__empty {
  font-size: 0.85rem;
  color: #94a3b8;
  margin-bottom: 1rem;
}

.week-view__empty--inline {
  margin-left: 0.25rem;
}

.week-view__grid-shell,
.day-view__grid-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.week-view__grid-shell .week-view__grid {
  min-width: 760px;
}

.day-view__grid-shell .day-view__grid {
  min-width: 340px;
}

.week-view__grid {
  --week-grid-header-height: 64px;
  display: grid;
  grid-template-columns: 72px repeat(7, minmax(0, 1fr));
  gap: 0;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 14px;
  background: #fafbff;
  max-height: 720px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  isolation: isolate;
}

.day-view__grid {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 0;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 14px;
  background: #fafbff;
  max-height: 720px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
}

.week-view__time-column {
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-right: 1px solid rgba(148, 163, 184, 0.25);
  position: sticky;
  left: 0;
  z-index: 2;
  width: 72px;
  min-width: 72px;
}

.week-view__grid .week-view__time-column {
  z-index: 100;
}

.week-view__time-header {
  flex: 0 0 var(--week-grid-header-height, 64px);
  height: var(--week-grid-header-height, 64px);
  min-height: var(--week-grid-header-height, 64px);
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(248, 250, 252, 0.98);
  box-sizing: border-box;
}

.week-view__grid .week-view__time-header {
  position: sticky;
  top: 0;
  z-index: 2;
  transition: height 0.16s ease, min-height 0.16s ease, flex-basis 0.16s ease;
}

.week-view__time-slot {
  height: 60px;
  min-height: 60px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0.35rem 0.5rem;
  font-size: 0.7rem;
  color: #94a3b8;
}

.week-view__day-column {
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(148, 163, 184, 0.18);
  background: #ffffff;
  min-width: 0;
}

.day-view__day-column {
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.week-view__day-column:last-child {
  border-right: none;
}

.week-view__day-header {
  position: sticky;
  top: 0;
  z-index: 80;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 0 var(--week-grid-header-height, 64px);
  height: var(--week-grid-header-height, 64px);
  min-height: var(--week-grid-header-height, 64px);
  box-sizing: border-box;
  padding: 0.6rem 0;
  gap: 0.2rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  transition: height 0.16s ease, min-height 0.16s ease, flex-basis 0.16s ease, padding 0.16s ease, gap 0.16s ease;
}

.week-view__grid--scrolled {
  --week-grid-header-height: 42px;
}

.week-view__grid--scrolled .week-view__day-header {
  flex-direction: row;
  gap: 0.35rem;
  padding: 0.2rem 0.25rem;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}

.week-view__grid--scrolled .week-view__time-header {
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.week-view__grid--scrolled .week-view__day-name {
  font-size: 0.65rem;
  letter-spacing: 0.06em;
}

.week-view__grid--scrolled .week-view__day-number {
  font-size: 0.86rem;
}

.week-view__day-header--today {
  color: #2563eb;
  font-weight: 700;
}

.day-view__day-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  height: 64px;
  box-sizing: border-box;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  background: #ffffff;
}

.day-view__day-header--today .day-view__day-number {
  background: #2563eb;
  color: #ffffff;
}

.day-view__day-name {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  color: #64748b;
}

.day-view__day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #e2e8f0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.week-view__day-name {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
}

.week-view__day-number {
  font-size: 1rem;
  font-weight: 600;
}

.week-view__day-body {
  position: relative;
  min-height: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(148, 163, 184, 0.12) 0,
    rgba(148, 163, 184, 0.12) 1px,
    transparent 1px,
    transparent 60px
  );
  background-size: 100% 60px;
  overflow: hidden;
  overflow: clip;
  padding: 0 6px;
}

.week-agenda {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.week-agenda__day {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.week-agenda__day-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 600;
  font-size: 0.85rem;
}

.week-agenda__day-header--today {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.week-agenda__day-name {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
}

.week-agenda__day-number {
  font-size: 0.95rem;
  font-weight: 700;
}

.week-agenda__events {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.week-agenda__event-card {
  width: 100%;
  border-radius: 14px;
  border: 2px solid transparent;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.week-agenda__event-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.week-agenda__event-card--block {
  border-color: #111827;
}

.week-agenda__event-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.week-agenda__event-title {
  font-weight: 600;
  font-size: 0.85rem;
}

.week-agenda__event-status {
  font-size: 1.05rem;
  opacity: 0.9;
}

.week-agenda__event-time {
  font-size: 0.78rem;
}

.week-agenda__event-meta {
  font-size: 0.75rem;
  opacity: 0.85;
}

.week-agenda__empty {
  font-size: 0.78rem;
  color: #94a3b8;
  padding-left: 0.9rem;
}

.week-view__event {
  position: absolute;
  padding: 0.28rem 0.38rem;
  border-radius: 10px;
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  gap: 0.08rem;
  font-size: 0.68rem;
  line-height: 1.12;
  min-width: 0;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  left: 0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  word-break: normal;
}

.week-view__event--block {
  padding: 0;
  pointer-events: none;
  box-shadow: none;
  border-radius: 6px;
  border-top: 1px solid rgba(15, 23, 42, 0.12);
  border-right: 1px solid rgba(15, 23, 42, 0.12);
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
  background-image: repeating-linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.06) 0,
    rgba(15, 23, 42, 0.06) 5px,
    rgba(255, 255, 255, 0.12) 5px,
    rgba(255, 255, 255, 0.12) 10px
  );
}

.week-view__event--block .week-view__event-header,
.week-view__event--block .week-view__event-time,
.week-view__event--block .week-view__event-meta {
  display: none;
}

.time-block-handle {
  position: absolute;
  left: 2px;
  z-index: auto;
  width: 28px;
  min-height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f172a;
  cursor: pointer;
  pointer-events: none;
}

.time-block-handle__track {
  position: absolute;
  z-index: 8;
  top: 0;
  bottom: 0;
  left: 7px;
  width: 10px;
  box-sizing: border-box;
  padding: 0 3px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    var(--time-block-accent, #334155),
    var(--time-block-accent-secondary, var(--time-block-accent, #334155))
  );
  background-clip: content-box;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.82);
  transition: width 0.15s ease, left 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  pointer-events: auto;
}

.time-block-handle__badge {
  position: absolute;
  z-index: 40;
  top: 4px;
  left: 1px;
  min-width: 24px;
  height: 24px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 0 3px;
  border: 1px solid color-mix(in srgb, var(--time-block-accent, #334155) 55%, #cbd5e1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.2);
  cursor: pointer;
  pointer-events: auto;
}

.time-block-handle__badge .material-icons {
  font-size: 13px;
}

.time-block-handle__count {
  font-size: 0.61rem;
  font-weight: 900;
}

.time-block-handle__tooltip {
  position: absolute;
  top: 4px;
  left: 32px;
  z-index: 41;
  max-width: 150px;
  overflow: hidden;
  padding: 0.32rem 0.48rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.94);
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.2);
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(-3px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
}

.time-block-handle:hover .time-block-handle__track,
.time-block-handle:focus-visible .time-block-handle__track {
  left: 6px;
  width: 12px;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(37, 99, 235, 0.55);
}

.time-block-handle:hover .time-block-handle__tooltip,
.time-block-handle:focus-visible .time-block-handle__tooltip {
  opacity: 1;
  transform: translateX(0);
}

.time-block-handle:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
  border-radius: 8px;
}

.week-view__event:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.16);
}

.week-view__event--grouped {
  box-shadow:
    3px 3px 0 rgba(255, 255, 255, 0.92),
    5px 5px 0 rgba(15, 23, 42, 0.14),
    0 12px 22px rgba(15, 23, 42, 0.12);
}

.week-view__event--grouped:hover,
.week-view__event--grouped:focus-visible {
  box-shadow:
    3px 3px 0 rgba(255, 255, 255, 0.94),
    5px 5px 0 rgba(15, 23, 42, 0.18),
    0 14px 28px rgba(15, 23, 42, 0.16);
}

.week-view__event:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.5);
  outline-offset: 2px;
}

.week-view__event-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.25rem;
  min-width: 0;
}

.week-view__event-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.12;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-view__event-status {
  flex: 0 0 auto;
  font-size: 0.82rem;
  line-height: 1;
  opacity: 0.8;
}

.week-view__event-overlap-count {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.78);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.2);
}

.week-view__event-time {
  display: block;
  font-size: 0.62rem;
  line-height: 1.1;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-view__event-meta {
  display: block;
  font-size: 0.6rem;
  line-height: 1.08;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-view__event--selected {
  box-shadow: 0 18px 36px rgba(37, 99, 235, 0.28);
  transform: translateY(-1px);
}

.week-view__event--selected::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 2px solid rgba(37, 99, 235, 0.35);
  pointer-events: none;
}

/* Event grid */
.event-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}

.event-item {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 1.1rem 1.2rem 1.35rem;
  background: #fff;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.event-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.12);
}

.status-pill-group {
  display: flex;
  gap: 0.4rem;
}

.status-pill {
  flex: 1 1 0;
  border-radius: 999px;
  padding: 0.4rem 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid transparent;
  background: rgba(15, 23, 42, 0.04);
  color: #475569;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.15s ease, border-color 0.15s ease;
}

.status-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
}

.status-pill:disabled {
  cursor: wait;
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

.status-pill--active {
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.12);
}

.status-pill__icon {
  font-size: 1.05rem;
}

.status-pill-group + .event-main {
  margin-top: 0.35rem;
}

.reminder-pill + .event-main {
  margin-top: 0.35rem;
}

.reminder-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(219, 39, 119, 0.12);
  color: #db2777;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.01em;
}

.reminder-pill .material-icons {
  font-size: 1.1rem;
}

.block-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: #111827;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.01em;
}

.block-pill .material-icons {
  font-size: 1.05rem;
}

.event-action-btn {
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #f8fafc;
  border-radius: 10px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #1f2937;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.event-action-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 10px 18px rgba(37, 99, 235, 0.16);
}

.event-action-btn--danger:hover {
  border-color: rgba(239, 68, 68, 0.65);
  box-shadow: 0 10px 18px rgba(239, 68, 68, 0.16);
  color: #b91c1c;
}

.event-main h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.event-main {
  grid-column: 1 / -1;
}

.event-main__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.event-calendar-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid currentColor;
  padding: 0.15rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(15, 23, 42, 0.02);
}

.block-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.1);
  color: #111827;
  padding: 0.1rem 0.55rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.event-main p {
  margin: 0.25rem 0 0;
  color: #475569;
  font-size: 0.9rem;
}

.reminder-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
}

.reminder-status-chip .material-icons {
  font-size: 1rem;
}

.event-meta {
  display: grid;
  gap: 0.25rem;
  grid-column: 1 / -1;
}

.event-meta p {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}

.event-meta strong {
  color: #0f172a;
  font-weight: 600;
}

.event-meta__row {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0.35rem;
}

.event-meta__label {
  white-space: nowrap;
}

.event-meta__value {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.event-meta__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  color: #0f172a;
}

.event-meta__toggle input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.event-meta__toggle span {
  color: #475569;
  font-weight: 500;
}

.event-user-link {
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
  position: relative;
}

.event-user-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 2px;
  background: currentColor;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.event-user-link:hover::after {
  opacity: 1;
}

.event-actions {
  display: inline-flex;
  gap: 0.35rem;
  margin-top: 0.4rem;
  justify-content: flex-start;
}

.user-field {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.user-search {
  display: flex;
  align-items: center;
  position: relative;
}

.user-search input {
  width: 100%;
  padding-right: 2.25rem;
}

.user-search__clear {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #475569;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.user-search__clear:hover {
  background: rgba(148, 163, 184, 0.2);
}

.user-search__hint {
  color: #64748b;
  font-size: 0.78rem;
}

.user-suggestions {
  position: absolute;
  z-index: 90;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.35rem;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.18);
  max-height: 260px;
  overflow-y: auto;
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-suggestions__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0.15rem;
  padding: 0.55rem 0.75rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.12s ease;
}

.user-suggestions__item:hover {
  background: rgba(37, 99, 235, 0.08);
  transform: translateY(-1px);
}

.user-suggestions__primary {
  font-weight: 600;
  color: #0f172a;
}

.user-suggestions__secondary {
  font-size: 0.78rem;
  color: #475569;
}

.user-suggestions__loading,
.user-suggestions__empty {
  padding: 0.65rem;
  font-size: 0.85rem;
  color: #475569;
  text-align: center;
}

.empty-state {
  border: 1px dashed rgba(100, 116, 139, 0.3);
  border-radius: 16px;
  padding: 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}

.calendar-card--full .card-header {
  grid-column: 1 / -1;
}

.calendar-card--full .calendar-wrapper {
  grid-column: 1 / 2;
  min-width: 0;
}

.calendar-card--full .event-list {
  grid-column: 2 / 3;
  align-self: stretch;
  height: 100%;
  min-width: 0;
}

.calendar-card--week .card-header,
.calendar-card--week .event-list {
  grid-column: 1 / -1;
}

.calendar-card--week .event-list {
  height: auto;
  min-height: 0;
}

.calendar-wrapper {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.calendar-wrapper :deep(.vc-container) {
  display: block !important;
  width: 100% !important;
  min-width: 100% !important;
  height: auto !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
}

.calendar-wrapper :deep(.vc-pane-container) {
  width: 100% !important;
  display: block !important;
}

.calendar-wrapper :deep(.vc-pane-layout) {
  display: grid !important;
  width: 100% !important;
  grid-template-columns: 1fr !important;
}

.calendar-wrapper :deep(.vc-nav-items .vc-nav-item) {
  width: 100px !important;
}

.calendar-wrapper :deep(.vc-pane) {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  justify-self: stretch !important;
}

.calendar-wrapper :deep(.vc-weeks),
.calendar-wrapper :deep(.vc-weekdays),
.calendar-wrapper :deep(.vc-week) {
  width: 100% !important;
  min-width: 100% !important;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1.75rem;
  overflow-y: auto;
}

.modal-backdrop--overlay {
  z-index: 2020;
}

.modal-card {
  background: #ffffff;
  border-radius: 20px;
  max-width: 480px;
  width: 100%;
  max-height: calc(100vh - 3.5rem);
  box-shadow: 0 32px 64px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.75rem 1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: 1rem;
  padding: 1.5rem 1.75rem 1.75rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.modal-form label {
  font-size: 0.9rem;
  color: #1e293b;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modal-form input,
.modal-form select,
.modal-form textarea {
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

}

.modal-form textarea {
  height: 70px;
  width: 100%;
  box-sizing: border-box;
}

.color-field {
  gap: 0.35rem;
}

.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.color-swatch {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 2px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  color: #ffffff;
}

.color-swatch:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.15);
}

.color-swatch--selected {
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.2);
  border-color: rgba(15, 23, 42, 0.3);
}

.color-swatch__check {
  font-size: 1.1rem;
}

.color-swatch--reset {
  background: #f8fafc;
  color: #0f172a;
  border-color: rgba(148, 163, 184, 0.35);
  padding: 0 0.85rem;
  height: 38px;
  min-width: 96px;
  font-weight: 700;
  box-shadow: none;
  border-radius: 999px;
}

.color-picker__hint {
  color: #64748b;
  font-size: 0.78rem;
  margin-top: 0.15rem;
  display: inline-block;
}

.color-picker-inline {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.color-picker-inline__label {
  font-size: 0.82rem;
  color: #475569;
  font-weight: 600;
}

.color-picker-inline__swatches {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.color-swatch--small {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  font-size: 0.85rem;
}

.color-swatch--small.color-swatch--reset {
  min-width: 84px;
  padding: 0 0.6rem;
  height: 32px;
  font-size: 0.78rem;
  font-weight: 700;
}

.modal-form input:focus,
.modal-form select:focus,
.modal-form textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.week-event-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.75rem;
  z-index: 2010;
}

.week-event-modal {
  position: relative;
  width: min(520px, 100%);
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 32px 64px rgba(15, 23, 42, 0.2);
  padding: 1.75rem 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.week-overlap-modal {
  width: min(480px, 100%);
  max-height: min(680px, calc(100vh - 3.5rem));
}

.week-overlap-modal__header {
  padding: 0 2.25rem;
}

.week-overlap-modal__description {
  margin: 0.3rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.35;
}

.week-overlap-modal__list {
  list-style: none;
  margin: 0;
  padding: 0.15rem 0.25rem 0.15rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  overflow-y: auto;
}

.week-overlap-modal__item {
  margin: 0;
  padding: 0;
}

.week-overlap-modal__event {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-left: 5px solid var(--overlap-event-accent, #2563eb);
  border-radius: 12px;
  background: #ffffff;
  color: #0f172a;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.week-overlap-modal__event:hover,
.week-overlap-modal__event:focus-visible {
  transform: translateY(-1px);
  border-color: rgba(100, 116, 139, 0.45);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.1);
  outline: none;
}

.week-overlap-modal__event-title {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: 700;
}

.week-overlap-modal__event-meta {
  color: #64748b;
  font-size: 0.78rem;
}

.week-event-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(15, 23, 42, 0.04);
  color: #0f172a;
}

.week-event-modal__status {
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.25rem;
  width: 100%;
}

.week-event-modal__status .status-pill {
  flex: 0 0 auto;
  min-width: 0;
}

.week-event-modal__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.week-event-modal__title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.week-event-modal__title h4 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
}

.week-event-modal__time {
  margin: 0;
  font-size: 0.95rem;
  color: #475569;
}

.week-event-modal__meta {
  margin-top: 0.5rem;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 0.35rem;
}

.week-event-modal__meta .event-meta__row {
  grid-template-columns: minmax(0, 120px) 1fr;
}

.week-event-modal__meta .event-meta__value {
  white-space: normal;
}

.week-event-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
}

.week-event-modal__status.reminder-pill,
.week-event-modal__status.block-pill {
  display: inline-flex;
  justify-content: center;
  margin: 0 auto 0.25rem;
}

@media (max-width: 640px) {
  .week-event-modal {
    padding: 1.5rem 1.3rem 1.2rem;
  }
}

@media (max-width: 1180px) {
  .calendars-dashboard {
    padding: 2rem 1.75rem;
  }

  .calendar-card {
    margin-bottom: 2.25rem;
  }

  .calendar-card--full {
    display: flex;
    flex-direction: column;
  }

  .calendar-card--full .calendar-wrapper {
    min-height: 520px;
  }

  .calendar-card--full .event-list {
    width: 100%;
  }

  .event-items {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1180px) {
  .event-toolbar,
  .week-toolbar-secondary {
    align-items: stretch;
  }

  .event-toolbar__info,
  .event-toolbar__actions,
  .week-toolbar-secondary__left,
  .week-toolbar-secondary__right {
    width: 100%;
  }

  .event-toolbar__actions,
  .week-toolbar-secondary__left,
  .week-toolbar-secondary__right {
    flex-wrap: wrap;
  }

  .event-search,
  .operator-filter {
    flex: 1 1 260px;
    max-width: none;
  }

  .status-filter-group {
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .calendars-dashboard {
    padding: 1.5rem 1rem 4rem;
  }

  .dashboard-header {
    margin-bottom: 1.75rem;
  }

  .dashboard-header h1 {
    font-size: 1.6rem;
  }

  .dashboard-header p {
    font-size: 0.9rem;
  }

  .calendar-switcher {
    justify-content: stretch;
  }

  .calendar-toggle {
    flex: 1 1 calc(50% - 0.4rem);
    text-align: center;
    padding: 0.65rem 1rem;
  }

  .calendar-card {
    padding: 1.25rem;
    margin-bottom: 2rem;
  }

  .card-header {
    padding: 1rem 1.05rem 1.1rem;
    margin: -1.25rem -1.25rem 0;
  }

  .calendar-controls {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .calendar-controls__buttons {
    grid-template-columns: 1fr;
  }

  .view-toggle {
    width: 100%;
  }

  .view-toggle__btn {
    flex: 1 1 0;
  }

  .calendar-controls .primary-button,
  .calendar-controls .secondary-button {
    width: 100%;
  }

  .event-toolbar__info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
  }

  .event-toolbar__hint {
    white-space: normal;
  }

  .event-toolbar__actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .event-search,
  .operator-filter {
    width: 100%;
    max-width: none;
    min-height: 52px;
    flex: 0 0 auto;
    box-sizing: border-box;
  }

  .event-search {
    border-radius: 18px;
  }

  .event-search__input,
  .operator-filter__select {
    width: 100%;
    min-width: 0;
  }

  .operator-filter {
    border-radius: 18px;
  }

  .status-filter-group {
    justify-content: flex-start;
  }

  .event-counter {
    align-self: flex-end;
  }

  .week-toolbar-secondary {
    gap: 0.75rem;
  }

  .week-toolbar-secondary__right {
    justify-content: flex-start;
  }

  .week-view-mode-toggle,
  .time-block-toggle,
  .week-zoom-toggle {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
  }

  .week-view-mode-toggle__btn,
  .week-zoom-toggle__btn {
    flex: 1 1 0;
    text-align: center;
  }

  .week-toolbar-chip {
    width: 100%;
    text-align: center;
  }

  .week-view-card {
    padding: 1rem;
  }

  .week-view__grid-shell .week-view__grid {
    min-width: 700px;
  }

  .week-view__time-column {
    width: 64px;
    min-width: 64px;
  }

  .week-view__time-slot {
    padding: 0.35rem;
    font-size: 0.66rem;
  }

  .week-view__day-header,
  .day-view__day-header {
    padding: 0.55rem 0.45rem;
  }

  .week-view__day-name,
  .day-view__day-name {
    font-size: 0.68rem;
  }

  .week-view__day-number {
    font-size: 0.92rem;
  }

  .day-view__day-number {
    width: 28px;
    height: 28px;
    font-size: 0.9rem;
  }

  .week-agenda {
    gap: 1rem;
  }

  .week-agenda__day-header {
    padding: 0.55rem 0.75rem;
  }

  .event-items {
    gap: 0.85rem;
  }

  .event-item {
    padding: 0.95rem 1rem 1.1rem;
  }

  .status-pill-group {
    flex-wrap: wrap;
  }

  .event-meta__row {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 0.15rem;
  }

  .event-meta__value {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
  }

  .event-actions {
    flex-wrap: wrap;
  }

  .calendar-card--full .calendar-wrapper {
    min-height: 420px;
  }

  .calendar-widget {
    min-height: 420px;
  }

  :deep(.vc-day) {
    width: 42px;
    height: 42px;
  }

  :deep(.vc-day-content) {
    min-height: 42px;
    padding-top: 8px;
  }

  :deep(.vc-day-layer-dots) {
    top: calc(8px + 24px + 5px);
  }

  .modal-backdrop,
  .week-event-modal-backdrop {
    align-items: flex-end;
    padding: 0.75rem;
  }

  .modal-card {
    border-radius: 16px;
    max-height: calc(100vh - 1.5rem);
    overflow-y: auto;
  }

  .modal-header {
    padding: 1.15rem 1rem 0.85rem;
  }

  .modal-form {
    padding: 1rem;
    gap: 0.85rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .modal-actions .primary-button,
  .modal-actions .secondary-button {
    width: 100%;
  }

  .week-event-modal {
    width: 100%;
    max-height: calc(100vh - 1.5rem);
    overflow-y: auto;
    padding: 1.4rem 1.05rem 1.1rem;
  }

  .week-event-modal__close {
    top: 0.55rem;
    right: 0.55rem;
  }

  .week-event-modal__header {
    align-items: flex-start;
    text-align: left;
  }

  .week-event-modal__title {
    justify-content: flex-start;
  }

  .week-event-modal__meta .event-meta__row {
    grid-template-columns: 1fr;
  }

  .week-event-modal__actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .realtime-badge {
    top: 82px;
    right: 16px;
    left: 16px;
    width: auto;
  }
}

.calendari-eventi-calendar-header {
  display: flex;
  align-items: center;
  min-height: 60px;
  padding-top: 10px;
  padding-bottom: 10px;
  box-sizing: border-box;
}

.calendari-eventi-calendar-title-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 40px;
}

.calendari-eventi-calendar-title {
  display: flex;
  align-items: center;
  min-height: 40px;
  line-height: 1.2;
}

.calendari-eventi-toolbar__filters {
  align-items: center;
}

.calendari-eventi-toolbar__control {
  height: 44px;
  min-height: 44px;
  min-width: 0;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid #d4d8de;
  border-radius: 12px;
  background: #f8f9fb;
  box-shadow: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.calendari-eventi-toolbar__control:hover {
  border-color: #a8b0bd;
}

.calendari-eventi-toolbar__control:focus-within {
  border-color: #64748b;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.14);
}

.calendari-eventi-toolbar__control > .material-icons {
  flex: 0 0 20px;
  width: 20px;
  font-size: 20px;
  line-height: 1;
  color: #64748b;
}

/* These fields are inside a single bordered control. Override the global
   staff input surface/geometry, including its focused state. */
.calendari-eventi-toolbar__search-input,
.calendari-eventi-toolbar__operator-select {
  width: 100%;
  flex: 1 1 0;
  height: 100% !important;
  min-width: 0;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  outline: none !important;
  box-shadow: none !important;
  box-sizing: border-box;
  font: inherit;
  line-height: 1.2;
}

.calendari-eventi-toolbar__search-input::placeholder {
  color: #727b89;
  opacity: 1;
}

.calendari-eventi-toolbar__search-input::-webkit-search-cancel-button,
.calendari-eventi-toolbar__search-input::-webkit-search-decoration {
  -webkit-appearance: none;
}

.calendari-eventi-toolbar__control > button {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  margin-right: -6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  box-shadow: none;
}

.calendari-eventi-toolbar__control > button:hover {
  background: #e9edf2;
  color: #0f172a;
}

.calendari-eventi-toolbar__control > button:focus-visible {
  outline: 2px solid #64748b;
  outline-offset: -2px;
}

.calendari-eventi-toolbar__control > button > .material-icons {
  font-size: 18px;
  color: inherit;
}

.calendari-eventi-toolbar__status-area {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  flex: 0 0 auto;
}

@media (min-width: 641px) and (max-width: 1180px) {
  .calendari-eventi-toolbar__control {
    flex: 1 1 220px;
    min-width: 0;
  }
}

@media (max-width: 640px) {
  .calendari-eventi-calendar-header {
    min-height: 60px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .calendari-eventi-toolbar__control {
    height: 52px;
    min-height: 52px;
  }

  .calendari-eventi-toolbar__control > button {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    margin-right: -8px;
  }

  .calendari-eventi-toolbar__status-area {
    justify-content: space-between;
    width: 100%;
  }

  .week-view__grid--scrolled {
    --week-grid-header-height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .week-view__day-header,
  .week-view__time-header {
    transition: none;
  }

  .realtime-badge-enter-active,
  .realtime-badge-leave-active {
    transition: opacity 0.01ms linear;
  }

  .realtime-badge-enter-from,
  .realtime-badge-leave-to {
    transform: none;
  }
}
</style>
